import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import React, { useState, useEffect, useCallback } from 'react';
import { getAllStudents } from '../utils/studentCache';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import { Plus, X, Search, Award, Eye, Edit2, Trash2, CheckCircle, Printer } from 'lucide-react';

import SearchableSelect from '../components/ui/SearchableSelect';
import DateInput from '../components/ui/DateInput';
import OutsiderCertificates, { canAccessOutsider } from './OutsiderCertificates';
import { fmtDMY } from '../lib/dateFormat';

const gradeRank = {'A+':3,'A':2,'B':1,'F':0};
function dedupeMarks(marks) {
  const best = {};
  marks.forEach(m => {
    const key = m.course_name||'';
    const curAttempt = best[key]?.attempt_number || 0;
    const mAttempt = m.attempt_number || 1;
    if (!best[key] || mAttempt > curAttempt) best[key] = m;
  });
  return Object.values(best);
}


const CERT_TYPES = [
  { value: 'NDT',             label: 'NDT Certificate' },
  { value: 'NACTET',          label: 'NACTET Certificate' },
  { value: 'NACTET_attested', label: 'NACTET Attested Certificate' },
  { value: 'bonafide',        label: 'Bonafide Certificate' },
  { value: 'experience',      label: 'Experience Certificate' },
  { value: 'experience_ext',  label: 'Experience Certificate Extension' },
  { value: 'overseas_edu',    label: 'Overseas Educational Document' },
  { value: 'salary_doc',      label: 'Salary Doc for Overseas Job/Education' },
  { value: 'internship',      label: 'Internship Certificate' },
  { value: 'other',           label: 'Other Certificates' },
];

const certLabel = val => CERT_TYPES.find(t => t.value === val)?.label || val;
const certBadge = val => ({ NDT: 'badge-blue', NACTET: 'badge-purple', NACTET_attested: 'badge-purple', bonafide: 'badge-cyan', experience: 'badge-green', experience_ext: 'badge-green', overseas_edu: 'badge-amber', salary_doc: 'badge-amber', internship: 'badge-blue', other: 'badge-gray' }[val] || 'badge-gray');
// Fetch every row of a Supabase query (a single response is capped at 1000 rows). Returns { data, error }.
async function fetchAllRows(buildQuery, pageSize = 1000) {
  let all = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) { console.error('fetchAllRows error:', error); return { data: all, error }; }
    all = all.concat(data || []);
    if (!data || data.length < pageSize) break;
  }
  return { data: all, error: null };
}
function escHtml(v) {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function downloadSheet(filename, rows, sheetName = 'Sheet1') {
  const saveBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };
  const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const plain = () => {   // fallback: unstyled workbook
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    saveBlob(new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: XLSX_MIME }));
  };
  try {
    if (!rows.length) return plain();
    const cols = Object.keys(rows[0]);
    const esc = v => String(v == null ? '' : v).replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const colL = i => { let n = i + 1, t = ''; while (n > 0) { const m = (n - 1) % 26; t = String.fromCharCode(65 + m) + t; n = Math.floor((n - 1) / 26); } return t; };
    const isDate = v => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v);
    const serial = v => { const [y, m, d] = v.slice(0, 10).split('-').map(Number); return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(1899, 11, 30)) / 86400000); };
    const statusCols = new Set(['Collected', 'Distribution Status', 'Request Status']);
    const statusStyle = v => { const t = String(v).toLowerCase();
      if (/^(collected|yes|processed)/.test(t)) return 5;                       // green
      if (/not distributed/.test(t)) return 7;                                 // red
      if (/partial|awaiting|at office|pending|submitted|^no$/.test(t)) return 6; // amber
      return 3; };
    // column widths from content (capped)
    const widths = cols.map(c => Math.min(42, Math.max(10, String(c).length + 3, ...rows.map(r => isDate(r[c]) ? 13 : String(r[c] == null ? '' : r[c]).length + 2))));
    const nC = cols.length, lastCol = colL(nC - 1);
    let xml = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">';
    xml += `<sheetViews><sheetView workbookViewId="0" showGridLines="0"><pane ySplit="3" topLeftCell="A4" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews><sheetFormatPr defaultRowHeight="18"/>`;
    xml += '<cols>' + widths.map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`).join('') + '</cols><sheetData>';
    const now = new Date(); const mn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    xml += `<row r="1" ht="26" customHeight="1"><c r="A1" s="4" t="inlineStr"><is><t>${esc(sheetName)}</t></is></c></row>`;
    xml += `<row r="2" ht="18" customHeight="1"><c r="A2" s="8" t="inlineStr"><is><t>Generated ${String(now.getDate()).padStart(2, '0')}-${mn[now.getMonth()]}-${now.getFullYear()}  |  ${rows.length} row${rows.length === 1 ? '' : 's'}</t></is></c></row>`;
    xml += '<row r="3" ht="30" customHeight="1">' + cols.map((c, i) => `<c r="${colL(i)}3" s="1" t="inlineStr"><is><t>${esc(c)}</t></is></c>`).join('') + '</row>';
    rows.forEach((r, ri) => {
      const rn = ri + 4;
      xml += `<row r="${rn}">` + cols.map((c, i) => {
        const v = r[c], ref = `${colL(i)}${rn}`;
        if (v == null || v === '') return `<c r="${ref}" s="${statusCols.has(c) ? 3 : 2}"/>`;
        if (isDate(v)) return `<c r="${ref}" s="9"><v>${serial(v)}</v></c>`;
        if (typeof v === 'number' && isFinite(v)) return `<c r="${ref}" s="3"><v>${v}</v></c>`;
        const st = statusCols.has(c) ? statusStyle(v) : (/^[0-9]+$/.test(String(v)) && String(v).length < 6 ? 3 : 2);
        return `<c r="${ref}" s="${st}" t="inlineStr"><is><t xml:space="preserve">${esc(v)}</t></is></c>`;
      }).join('') + '</row>';
    });
    xml += `</sheetData><autoFilter ref="A3:${lastCol}${rows.length + 3}"/><mergeCells count="2"><mergeCell ref="A1:${lastCol}1"/><mergeCell ref="A2:${lastCol}2"/></mergeCells>`;
    xml += '<pageMargins left="0.4" right="0.4" top="0.5" bottom="0.5" header="0.3" footer="0.3"/><pageSetup orientation="landscape" fitToHeight="0"/></worksheet>';
    xml = xml.replace('<sheetViews>', '<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr><sheetViews>');
    const fill = c => `<fill><patternFill patternType="solid"><fgColor rgb="FF${c}"/><bgColor indexed="64"/></patternFill></fill>`;
    const bd = '<border><left style="thin"><color rgb="FFBFC7D1"/></left><right style="thin"><color rgb="FFBFC7D1"/></right><top style="thin"><color rgb="FFBFC7D1"/></top><bottom style="thin"><color rgb="FFBFC7D1"/></bottom><diagonal/></border>';
    const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="1"><numFmt numFmtId="164" formatCode="dd\\-mmm\\-yyyy"/></numFmts>
<fonts count="5"><font><sz val="10"/><name val="Calibri"/></font><font><b/><sz val="10"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font><font><b/><sz val="14"/><color rgb="FF1F3A5F"/><name val="Calibri"/></font><font><sz val="9"/><i/><color rgb="FF6B7280"/><name val="Calibri"/></font><font><b/><sz val="10"/><name val="Calibri"/></font></fonts>
<fills count="7"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill>${fill('1F3A5F')}${fill('DCFCE7')}${fill('FEF3C7')}${fill('FEE2E2')}${fill('F3F4F6')}</fills>
<borders count="2"><border><left/><right/><top/><bottom/><diagonal/></border>${bd}</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="10">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="4" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="4" fillId="5" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="3" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment vertical="center"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
    const safeName = esc(String(sheetName).replace(/[\[\]:*?\/\\]/g, ' ').slice(0, 31) || 'Sheet1');
    const z = new JSZip();
    z.file('[Content_Types].xml', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/></Types>');
    z.file('_rels/.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>');
    z.file('xl/workbook.xml', `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets><sheet name="${safeName}" sheetId="1" r:id="rId1"/></sheets><definedNames><definedName name="_xlnm._FilterDatabase" localSheetId="0" hidden="1">'${safeName}'!$A$3:$${lastCol}$${rows.length + 3}</definedName><definedName name="_xlnm.Print_Titles" localSheetId="0">'${safeName}'!$3:$3</definedName></definedNames></workbook>`);
    z.file('xl/_rels/workbook.xml.rels', '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/><Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>');
    z.file('xl/styles.xml', styles);
    z.file('xl/worksheets/sheet1.xml', xml);
    z.generateAsync({ type: 'blob', mimeType: XLSX_MIME }).then(saveBlob).catch(plain);
  } catch (e) { plain(); }
}
// Request certificate type -> distribution certificate type
const REQ_TO_DIST_TYPE = { NDT: 'NDT Certificate', NACTET: 'NACTET Certificate', NACTET_attested: 'NACTET Certificate', bonafide: 'Bonafide Certificate', experience: 'Experience Certificate', experience_ext: 'Experience Certificate Extension', overseas_edu: 'Overseas Education Document', salary_doc: 'Salary Doc for Overseas Job/Education', internship: 'Other', other: 'Other' };
const REQ_NO_PREFIX = { NDT: 'NDT', NACTET: 'NAC', NACTET_attested: 'NAT', bonafide: 'BON', experience: 'EXP', experience_ext: 'EXT', overseas_edu: 'OVS', salary_doc: 'SAL', internship: 'INT', other: 'OTH' };
const statusLabel = s => ({ submitted: 'Submitted', PROCESSED: 'Processed', collected: 'At Office' }[s] || s || '\u2014');
// WhatsApp number -> wa.me format (country code 91 added only when missing)
const waPhone = p => { const d = String(p || '').replace(/\D/g, ''); if (!d) return ''; return d.length > 10 && d.startsWith('91') ? d : '91' + d.slice(-10); };
const statusBadge = s => ({ submitted: 'badge-blue', PROCESSED: 'badge-green', collected: 'badge-purple' }[s] || 'badge-gray');

const emptyForm = { cert_type: '', student_id: '', exam_request_id: '', request_date: new Date().toISOString().split('T')[0], issue_date: '', course_complete_date: '', exam_end_date: '', cert_issue_date: '', purpose: '', processing_status: 'submitted', remarks: '', total_nactet_certs: '', total_ndt_certs: '', experience_cert_created: 'no', concerned_person: '', dispatch_mode: 'collect', courier_name: '', tracking_number: '', dispatch_date: '', cert_status: 'active', cert_status_notes: '' };
const CERT_DIST_TYPES = ['Bonafide Certificate', 'Experience Certificate', 'Experience Certificate Extension', 'Overseas Education Document', 'Salary Doc for Overseas Job/Education', 'Course Completion Certificate', 'NDT Certificate', 'NACTET Certificate', 'Other'];
const emptyDist = { student_id: '', batch_id: '', cert_type: '', request_date: new Date().toISOString().split('T')[0], issue_date: '', issuer_name: '', collector_name: '', collector_contact: '', student_collection_date: '', collected_by_student: false, quantity: '', quantity_collected: '', id_proof_type: '', id_proof_number: '', remarks: '' };



const ID_PROOF_TYPES = ['Aadhaar', 'Passport', 'Driving Licence', 'Voter ID', 'College / Company ID', 'Other'];

const ISSUER_NAMES = [
  // Academic Counselors
  'Amiya V', 'Aneecia Thomas', 'Anjana S Nair', 'Ashna V Thomas',
  'Neethu Ramesh', 'Rakhi B.S', 'Sanika Soman', 'Smitha Unnithan M.R',
  // HR Officers
  'Sameer Sait', 'Sanil George', 'Suma Thomas',
  // Office Admin
  'Arsha K.G', 'Nisha P Kumar',
  // Admin
  'Admin',
];

// ── CERTIFICATE REQUEST PRINT REPORT ─────────────────────────────────────────


// ── CERTIFICATE REQUEST PRINT REPORT ─────────────────────────────────────────



const CERT_RPT_CSS2 = `@page{size:A4 landscape;margin:8mm 10mm}*{box-sizing:border-box;margin:0;padding:0;font-family:Arial,sans-serif}body{font-size:12px;color:#000}.header{border:2px solid #000;margin-bottom:4px}.header-top{background:#b45309;color:#fff;text-align:center;padding:4px;font-size:14px;font-weight:900;letter-spacing:2px}.meta{display:flex;gap:20px;padding:6px 8px;font-size:12px;border-top:1px solid #000}.meta span{font-weight:700}table{width:100%;border-collapse:collapse;margin-top:4px}th{background:#b45309;color:#fff;padding:8px 6px;font-size:12px;text-align:center;border:1px solid #000;font-weight:700}td{border:1px solid #999;padding:8px 6px;font-size:12px;vertical-align:middle}.sno{text-align:center;width:20px}.name{font-weight:600}.regn{font-family:monospace;text-align:center}.grade{text-align:center;font-weight:700}.footer{margin-top:8px;display:flex;justify-content:space-between;font-size:12px;padding:4px 0;border-top:1px solid #000}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact}.no-print{display:none!important}}`;

// ── GROUPED CERTIFICATE REQUEST PRINT REPORT (multiple students, one table) ──
// No. of NACTET certificates of one student: from his course package in Master Data; the typed request total is only used when the package has no value
const certNactetCount = (cert, student, pkgMap) => {
  const k = String((student && student.course_name) || '').trim().toLowerCase();
  const byId = pkgMap && student && student.course_package_id ? Number(pkgMap['id:' + student.course_package_id]) : NaN;
  if (byId > 0) return byId;
  const n = pkgMap && k ? Number(pkgMap[k]) : NaN;
  if (n > 0) return n;
  return Number(cert && cert.total_nactet_certs) || 0;
};
function exportCertGroupExcel(rows, marksAll, topicsArr, coordinatorsRef, batchName, pkgMap) {
  const first = rows[0]?.cert || {};
  const batch = rows[0]?.student?.batch_name || '—';
  const _bn = rows[0]?.student?.batch_name||'';
  const branch = _bn.startsWith('T')?'TVM':_bn.startsWith('E')?'EKM':(rows[0]?.student?.centre||'—');
  const cp = coordinatorsRef.find(s => s.id === first.concerned_person);
  const concerned = cp ? (cp.staff_name||cp.full_name) + (cp.designation?` (${cp.designation})`:'') : '—';
  const fmtDate = (d) => { if(!d) return '—'; const dt=new Date(d); const mn=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return dt.getDate().toString().padStart(2,'0')+'-'+mn[dt.getMonth()]+'-'+dt.getFullYear(); };
  const reqDate = fmtDate(first.request_date);
  const fileDate = fmtDate(first.request_date);
  const esc = (s) => String(s||'').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');

  const MEP_CODES = ['HVAC','FF','E&P','EP'];
  const isMep = (m) => { const td=topicsArr.find(x=>x.topic_name===m.course_name); const sc=(td?.short_code||'').toUpperCase(); const tn=(m.course_name||'').toUpperCase(); return MEP_CODES.includes(sc)||MEP_CODES.includes(tn); };
  const NDT_EXCLUDE = ['NDT-MPT','NDT-UT','NDT-RT','NDT-LPT','MPT','UT','RT','LPT'];
  const excludeNDT = t => { const sc=(topicsArr.find(x=>x.topic_name===t)?.short_code||t).toUpperCase(); return !NDT_EXCLUDE.some(n=>sc.includes(n)||t.toUpperCase().includes(n)); };
  const rawTopics = [...new Set(marksAll.map(m=>m.course_name).filter(Boolean))].filter(excludeNDT);
  const MECH_ORD = ['WE','QA/QC-M','PE','PLE','SE-M','OFE','HSE-M','PPE','HVAC','FF','E&P'];
  const CIVIL_ORD = ['HSE-C','SE-C','QA/QC-C','QS','PM'];
  const getSc = t => (topicsArr.find(x=>x.topic_name===t)?.short_code||t).toUpperCase();
  const isCivil = rawTopics.some(t=>CIVIL_ORD.includes(getSc(t)));
  const ORD = isCivil ? CIVIL_ORD : MECH_ORD;
  const ordered = [];
  ORD.forEach(sc => { const t=rawTopics.find(t=>getSc(t)===sc); if(t) ordered.push(t); });
  rawTopics.forEach(t => { if(!ordered.includes(t)) ordered.push(t); });
  const topicCols = ordered.slice(0,12);
  const anyMep = marksAll.some(isMep);
  const sortedRows = [...rows].sort((a,b)=>(a.student?.register_number||'').localeCompare(b.student?.register_number||'',undefined,{numeric:true}));
  const totalCols = 6 + topicCols.length + (anyMep?2:1);

  // SpreadsheetML styles
  // Style indices:
  // 0=default, 1=title(white bold on dark blue), 2=meta, 3=header(bold on light blue),
  // 4=normal cell, 5=grade A+(black bold), 6=grade A(red bold), 7=footer(italic center)
  const styles = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts>
    <font><sz val="9"/><name val="Arial"/></font>
    <font><sz val="11"/><b/><color rgb="FFFFFFFF"/><name val="Arial"/></font>
    <font><sz val="9"/><name val="Arial"/></font>
    <font><sz val="9"/><b/><name val="Arial"/></font>
    <font><sz val="9"/><b/><color rgb="FFDC2626"/><name val="Arial"/></font>
    <font><sz val="9"/><i/><name val="Arial"/></font>
  </fonts>
  <fills>
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FF1A3A6B"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFD6E4F0"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/></patternFill></fill>
  </fills>
  <borders>
    <border><left/><right/><top/><bottom/><diagonal/></border>
    <border>
      <left style="thin"><color rgb="FF000000"/></left>
      <right style="thin"><color rgb="FF000000"/></right>
      <top style="thin"><color rgb="FF000000"/></top>
      <bottom style="thin"><color rgb="FF000000"/></bottom>
    </border>
  </borders>
  <cellStyleXfs><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs>
    <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
    <xf numFmtId="0" fontId="1" fillId="2" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0"/>
    <xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="3" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="4" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="5" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
    <xf numFmtId="0" fontId="0" fillId="4" borderId="1" xfId="0" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
  </cellXfs>
</styleSheet>`;
  // s index: 1=title, 2=meta-left, 3=header, 4=center, 5=Aplus(black bold), 6=A(red bold), 7=footer-italic, 8=meta-left-text

  const colLetter = (n) => { let s=''; n++; while(n>0){s=String.fromCharCode(64+(n%26||26))+s;n=Math.floor((n-1)/26);} return s; };

  const C = (r,c,v,s) => {
    const ref = `${colLetter(c)}${r+1}`;
    const isNum = typeof v === 'number';
    return `<c r="${ref}" s="${s}"${isNum?' t=""':' t="s"'}><v>${esc(String(v))}</v></c>`;
  };
  const Cs = (r,c,v,s) => {
    const ref = `${colLetter(c)}${r+1}`;
    return `<c r="${ref}" s="${s}" t="inlineStr"><is><t>${esc(String(v))}</t></is></c>`;
  };

  // Build shared strings
  const ss = [];
  const si = (v) => { const s=String(v); let i=ss.indexOf(s); if(i<0){i=ss.length;ss.push(s);} return i; };

  // Build rows
  let rowsXml = '';
  let merges = [];
  let rowIdx = 0;

  // Row 0: Title
  merges.push(`A1:${colLetter(totalCols-1)}1`);
  rowsXml += `<row r="1" ht="36" customHeight="1"><c r="A1" s="1" t="inlineStr"><is><t>CERTIFICATE REQUEST</t></is></c></row>\n`;
  rowIdx++;

  // Row 1: Req No / Date
  merges.push(`A2:C2`); merges.push(`D2:${colLetter(totalCols-1)}2`);
  rowsXml += `<row r="2" ht="22" customHeight="1"><c r="A2" s="2" t="inlineStr"><is><t>Req. No: ${esc(first.cert_group_number||first.request_number||'—')}</t></is></c><c r="D2" s="2" t="inlineStr"><is><t>Req. Date: ${esc(reqDate)}</t></is></c></row>\n`;

  // Row 2: Branch / Batch
  merges.push(`A3:C3`); merges.push(`D3:${colLetter(totalCols-1)}3`);
  rowsXml += `<row r="3" ht="22" customHeight="1"><c r="A3" s="2" t="inlineStr"><is><t>Branch: ${esc(branch)}</t></is></c><c r="D3" s="2" t="inlineStr"><is><t>Batch: ${esc(batch)}</t></is></c></row>\n`;

  // Row 3: Course Complete / Exam End
  merges.push(`A4:C4`); merges.push(`D4:${colLetter(totalCols-1)}4`);
  rowsXml += `<row r="4" ht="22" customHeight="1"><c r="A4" s="2" t="inlineStr"><is><t>Course Complete Date: ${esc(fmtDate(first.course_complete_date))}</t></is></c><c r="D4" s="2" t="inlineStr"><is><t>Exam End Date: ${esc(fmtDate(first.exam_end_date))}</t></is></c></row>\n`;

  // Row 4: Concerned Person
  merges.push(`A5:${colLetter(totalCols-1)}5`);
  rowsXml += `<row r="5" ht="22" customHeight="1"><c r="A5" s="2" t="inlineStr"><is><t>Concerned Person: ${esc(concerned)}</t></is></c></row>\n`;

  // Row 5: Issued Date
  merges.push(`A6:${colLetter(totalCols-1)}6`);
  rowsXml += `<row r="6" ht="22" customHeight="1"><c r="A6" s="2" t="inlineStr"><is><t>Issued Date: </t></is></c></row>\n`;

  // Row 6: Headers
  const hdrs = ['#','Trainee Name','Register No.','Package','Contact No.','No. of NACTET Certificates',...topicCols.map(t=>getSc(t)),'MAIN',...(anyMep?['MEP']:[])];
  rowsXml += `<row r="7" ht="42" customHeight="1">${hdrs.map((h,c)=>`<c r="${colLetter(c)}7" s="3" t="inlineStr"><is><t>${esc(h)}</t></is></c>`).join('')}</row>\n`;

  // Data rows
  sortedRows.forEach(({cert,student},i) => {
    const rn = 8 + i;
    const sMarks = marksAll.filter(m=>m.student_id===cert.student_id);
    const mepMarks = dedupeMarks(sMarks.filter(isMep));
    const mainMarks = dedupeMarks(sMarks.filter(m => !isMep(m)));
    const mainAplus = mainMarks.filter(m=>m.grade==='A+').length;
    const mainGrade = mainMarks.length>0?(mainAplus>=Math.ceil(mainMarks.length/2)?'A+':'A'):'—';
    const mepAplus = mepMarks.filter(m=>m.grade==='A+').length;
    const mepGrade = mepMarks.length>0?(mepAplus>=Math.ceil(mepMarks.length/2)?'A+':'A'):'—';
    const gStyle = (g) => g==='A+'?5:g==='A'?6:4;
    let cells = `<c r="A${rn}" s="4" t="inlineStr"><is><t>${i+1}</t></is></c>`;
    cells += `<c r="B${rn}" s="8" t="inlineStr"><is><t>${esc(student?.full_name||'—')}</t></is></c>`;
    cells += `<c r="C${rn}" s="4" t="inlineStr"><is><t>${esc(student?.register_number||'—')}</t></is></c>`;
    cells += `<c r="D${rn}" s="4" t="inlineStr"><is><t>${esc(student?.course_name||'—')}</t></is></c>`;
    cells += `<c r="E${rn}" s="4" t="inlineStr"><is><t>${esc(student?.contact_number||'—')}</t></is></c>`;
    cells += `<c r="F${rn}" s="4" t="inlineStr"><is><t>${certNactetCount(cert,student,pkgMap)||mainMarks.length||'—'}</t></is></c>`;
    topicCols.forEach((t,ci) => {
      const m = sMarks.find(x=>x.course_name===t);
      const g = m?.grade||'—';
      cells += `<c r="${colLetter(6+ci)}${rn}" s="${gStyle(g)}" t="inlineStr"><is><t>${esc(g)}</t></is></c>`;
    });
    const mc = 6+topicCols.length;
    cells += `<c r="${colLetter(mc)}${rn}" s="${gStyle(mainGrade)}" t="inlineStr"><is><t>${esc(mainGrade)}</t></is></c>`;
    if(anyMep) cells += `<c r="${colLetter(mc+1)}${rn}" s="${gStyle(mepGrade)}" t="inlineStr"><is><t>${esc(mepGrade)}</t></is></c>`;
    rowsXml += `<row r="${rn}" ht="24" customHeight="1">${cells}</row>\n`;
  });

  // Footer
  const footerRow = 8 + sortedRows.length;
  merges.push(`A${footerRow}:${colLetter(totalCols-1)}${footerRow}`);
  rowsXml += `<row r="${footerRow}" ht="18" customHeight="1"><c r="A${footerRow}" s="7" t="inlineStr"><is><t>Armstech Engineers Pvt Ltd — Certificate Request</t></is></c></row>\n`;

  // Column widths
  const colWidths = [4,22,13,10,13,18,...topicCols.map(()=>6),8,...(anyMep?[6]:[])];
  const colsXml = colWidths.map((w,i)=>`<col min="${i+1}" max="${i+1}" width="${w}" customWidth="1"/>`).join('');

  const mergesXml = merges.map(m=>`<mergeCell ref="${m}"/>`).join('');

  const ws = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>
<sheetFormatPr defaultRowHeight="14" customHeight="1"/>
<cols>${colsXml}</cols>
<sheetData>${rowsXml}</sheetData>
<mergeCells count="${merges.length}">${mergesXml}</mergeCells>
<printOptions horizontalCentered="1"/>
<pageMargins left="0.25" right="0.25" top="0.3" bottom="0.3" header="0.1" footer="0.1"/>
<pageSetup paperSize="9" orientation="landscape" fitToWidth="1" fitToHeight="1" scale="75" useFirstPageNumber="0" r:id="rId3"/>
</worksheet>`;

  const wb_xml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets><sheet name="Cert Requests" sheetId="1" r:id="rId1"/></sheets>
</workbook>`;

  const rels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;

  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;

  const topRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

  // Use JSZip-style approach via XLSX utility
  const zip = XLSX.utils.book_new();
  // Inject raw XML via SheetJS internal (fallback to blob approach)
  try {
    // Try raw zip approach
    if(JSZip) {
      const z = new JSZip();
      z.file('[Content_Types].xml', contentTypes);
      z.file('_rels/.rels', topRels);
      z.file('xl/workbook.xml', wb_xml);
      z.file('xl/_rels/workbook.xml.rels', rels);
      z.file('xl/worksheets/sheet1.xml', ws);
      z.file('xl/styles.xml', styles);
      z.generateAsync({type:'blob',mimeType:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}).then(blob=>{
        const url=URL.createObjectURL(blob);
        const a=document.createElement('a');
        a.href=url; a.download=`${first.cert_group_number||first.request_number||'CERT'}-Certificate Request-${batch}-${fileDate}.xlsx`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
      });
      return;
    }
  } catch(e) {}
  // Fallback: use SheetJS write with aoa (no colors but valid xlsx)
  const wsData = XLSX.utils.book_new();
  const wsSheet = XLSX.utils.aoa_to_sheet([
    ['CERTIFICATE REQUEST'],
    [`Req. No: ${first.cert_group_number||first.request_number||'—'}`, '', '', `Req. Date: ${reqDate}`],
    [`Branch: ${branch}`, '', '', `Batch: ${batch}`],
    [`Course Complete Date: ${fmtDate(first.course_complete_date)}`, '', '', `Exam End Date: ${fmtDate(first.exam_end_date)}`],
    [`Concerned Person: ${concerned}`],
    [`Issued Date: `],
    ['#','Trainee Name','Register No.','Package','Contact No.','No. of NACTET Certs',...topicCols.map(t=>getSc(t)),'MAIN',...(anyMep?['MEP']:[])],
    ...sortedRows.map(({cert,student},i)=>{
      const sMarks=marksAll.filter(m=>m.student_id===cert.student_id);
      const mepMarks = dedupeMarks(sMarks.filter(isMep)); const mainMarks = dedupeMarks(sMarks.filter(m => !isMep(m)));
      const mainGrade=mainMarks.length>0?(mainMarks.filter(m=>m.grade==='A+').length>=Math.ceil(mainMarks.length/2)?'A+':'A'):'—';
      const mepGrade=mepMarks.length>0?(mepMarks.filter(m=>m.grade==='A+').length>=Math.ceil(mepMarks.length/2)?'A+':'A'):'—';
      return [i+1,student?.full_name||'—',student?.register_number||'—',student?.course_name||'—',student?.contact_number||'—',certNactetCount(cert,student,pkgMap)||mainMarks.length||'—',...topicCols.map(t=>{const m=sMarks.find(x=>x.course_name===t);return m?.grade||'—';}),mainGrade,...(anyMep?[mepGrade]:[])];
    }),
    ['Armstech Engineers Pvt Ltd — Certificate Request'],
  ]);
  wsSheet['!merges']=[{s:{r:0,c:0},e:{r:0,c:totalCols-1}},{s:{r:4,c:0},e:{r:4,c:totalCols-1}},{s:{r:5,c:0},e:{r:5,c:totalCols-1}}];
  wsSheet['!cols']=[{wch:5},{wch:25},{wch:15},{wch:12},{wch:15},{wch:20},...topicCols.map(()=>({wch:8})),{wch:10},...(anyMep?[{wch:10}]:[])];
  XLSX.utils.book_append_sheet(wsData,'Cert Requests',wsSheet);
  const out=XLSX.write(wsData,{bookType:'xlsx',type:'array'});
  const blob2=new Blob([out],{type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
  const url2=URL.createObjectURL(blob2);
  const a2=document.createElement('a');
  a2.href=url2; a2.download=`${first.cert_group_number||first.request_number||'CERT'}-Certificate Request-${batch}-${fileDate}.xlsx`;
  document.body.appendChild(a2); a2.click(); document.body.removeChild(a2); URL.revokeObjectURL(url2);
}


function printCertGroupReport(rows, marksAll, topicsArr, coordinatorsRef, pkgMap) {
  // rows: array of { cert, student }
  const first = rows[0]?.cert || {};
  const reqDate = first.request_date ? new Date(first.request_date).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : '\u2014';
  const batch = rows[0]?.student?.batch_name || '\u2014';
  const _batchName = rows[0]?.student?.batch_name||'';
  const branch = _batchName.startsWith('T')?'TVM':_batchName.startsWith('E')?'EKM':(rows[0]?.student?.centre||'\u2014');
  const cp = coordinatorsRef.find(s => s.id === first.concerned_person);
  const concerned = cp ? (cp.staff_name || cp.full_name) + (cp.designation ? ` (${cp.designation})` : '') : '\u2014';

  const MEP_CODES = ['HVAC', 'FF', 'E&P', 'EP'];
  const isMep = (m) => {
    const topicDef = topicsArr.find(x => x.topic_name === m.course_name);
    const sc = (topicDef?.short_code || '').toUpperCase();
    const tn = (m.course_name || '').toUpperCase();
    return MEP_CODES.includes(sc) || MEP_CODES.includes(tn);
  };
  const fmtDate = (d) => { if(!d) return '—'; const dt=new Date(d); const mn=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return dt.getDate().toString().padStart(2,'0')+'-'+mn[dt.getMonth()]+'-'+dt.getFullYear(); };
  const gColor = (g) => g==='A+'?'#000000':g==='A'?'#dc2626':g==='B'?'#d97706':g==='F'?'#dc2626':'#666';
  const gBold = (g) => (g==='A+'||g==='A') ? 'font-weight:bold;' : '';

  const NDT_EXCLUDE = ['NDT-MPT','NDT-UT','NDT-RT','NDT-LPT','MPT','UT','RT','LPT'];
  const excludeNDT = t => { const sc=(topicsArr.find(x=>x.topic_name===t)?.short_code||t).toUpperCase(); return !NDT_EXCLUDE.some(n=>sc.includes(n)||t.toUpperCase().includes(n)); };
  const rawTopicsPDF = [...new Set(marksAll.map(m => m.course_name).filter(Boolean))].filter(excludeNDT);
  const MECH_ORD = ['WE','QA/QC-M','PE','PLE','SE-M','OFE','HSE-M','PPE','HVAC','FF','E&P'];
  const CIVIL_ORD = ['HSE-C','SE-C','QA/QC-C','QS','PM'];
  const getScPDF = t => (topicsArr.find(x=>x.topic_name===t)?.short_code||t).toUpperCase();
  const isCivilPDF = rawTopicsPDF.some(t => CIVIL_ORD.includes(getScPDF(t)));
  const ORD = isCivilPDF ? CIVIL_ORD : MECH_ORD;
  const orderedPDF = [];
  ORD.forEach(sc => { const t = rawTopicsPDF.find(t => getScPDF(t)===sc); if(t) orderedPDF.push(t); });
  rawTopicsPDF.forEach(t => { if(!orderedPDF.includes(t)) orderedPDF.push(t); });
  const markedTopics = orderedPDF;
  const topicCols = markedTopics.slice(0, 12);
  const thead = topicCols.map(t => { const sc = topicsArr.find(x => x.topic_name===t)?.short_code||t; return `<th class="grade" style="min-width:28px">${escHtml(sc)}</th>`; }).join('');
  const anyMep = marksAll.some(isMep);

  const sortedRows = [...rows].sort((a,b)=>{
    const ra = a.student?.register_number||'';
    const rb = b.student?.register_number||'';
    return ra.localeCompare(rb, undefined, {numeric:true});
  });
  const tbody = sortedRows.map(({cert, student}, i) => {
    const sMarks = marksAll.filter(m => m.student_id === cert.student_id);
    const cells = topicCols.map(t => {
      const m = sMarks.find(x => x.course_name === t);
      const g = m?.grade || '\u2014';
      return `<td class="grade" style="color:${gColor(g)};${gBold(g)}">${g}</td>`;
    }).join('');

    const mepMarks = dedupeMarks(sMarks.filter(isMep));
    const mainMarks = dedupeMarks(sMarks.filter(m => !isMep(m)));
    const mainTotal = certNactetCount(cert, student, pkgMap) || mainMarks.length;
    const mainAplus = mainMarks.filter(m => m.grade === 'A+').length;
    const mainGrade = mainMarks.length > 0 ? (mainAplus >= Math.ceil(mainMarks.length / 2) ? 'A+' : 'A') : '\u2014';
    const mepAplus = mepMarks.filter(m => m.grade === 'A+').length;
    const mepGrade = mepMarks.length > 0 ? (mepAplus >= Math.ceil(mepMarks.length / 2) ? 'A+' : 'A') : null;

    const summary = `<td class="grade" style="color:${gColor(mainGrade)};${gBold(mainGrade)}">${mainGrade}</td>` + (anyMep ? `<td class="grade" style="color:${gColor(mepGrade||'')};${gBold(mepGrade||'')}">${mepGrade||'\u2014'}</td>` : '');

    return `<tr><td class="sno">${i+1}</td><td class="name">${escHtml(student?.full_name)||'\u2014'}</td><td class="regn">${escHtml(student?.register_number)||'\u2014'}</td><td>${escHtml(student?.course_name)||'\u2014'}</td><td>${escHtml(student?.contact_number)||'\u2014'}</td><td style="text-align:center">${certNactetCount(cert, student, pkgMap) || '\u2014'}</td>${cells}${summary}</tr>`;
  }).join('');

  const summaryThead = `<th class="grade" style="min-width:36px">MAIN</th>` + (anyMep ? `<th class="grade" style="min-width:36px">MEP</th>` : '');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>${escHtml(first.cert_group_number||first.request_number||'CERT')}-Certificate Request-${escHtml(batch)}-${fmtDate(first.request_date)}</title><style>${CERT_RPT_CSS2}</style></head><body>
  <div class="header"><div class="header-top">CERTIFICATE REQUEST</div>
  <div class="meta"><div>Req. No: <span>${escHtml(first.cert_group_number || first.request_number)||'\u2014'}</span></div><div>Req. Date: <span>${reqDate}</span></div><div>Branch: <span>${escHtml(branch)}</span></div><div>Batch: <span>${escHtml(batch)}</span></div></div>
  <div class="meta">${first.course_complete_date?`<div>Course Complete Date: <span>${fmtDate(first.course_complete_date)}</span></div>`:''} ${first.exam_end_date?`<div>Exam End Date: <span>${fmtDate(first.exam_end_date)}</span></div>`:''} <div>Concerned Person: <span>${escHtml(concerned)}</span></div> <div>Issued Date: <span></span></div></div></div>
  <table><thead><tr><th class="sno">#</th><th style="min-width:130px">Trainee Name</th><th>Register No.</th><th>Package</th><th>Contact No.</th><th>No. of NACTET Certificates</th>${thead}${summaryThead}</tr></thead><tbody>${tbody}</tbody></table>
  ${first.purpose ? `<div style="margin-top:6px;padding:4px 8px;border:1px solid #ccc;font-size:9px"><span style="color:#dc2626;font-weight:700">Purpose / Remarks:</span> ${escHtml(first.purpose)}</div>` : ""}
  <div class="footer"><div style="text-align:center">Armstech Engineers Pvt Ltd \u2014 Certificate Request</div></div>
  <div class="no-print" style="position:fixed;top:12px;right:12px;z-index:999"><button onclick="window.print()" style="padding:8px 18px;background:#1a3a6b;color:#fff;border:none;border-radius:6px;font-size:13px;cursor:pointer">🖨️ Print as PDF</button></div></body></html>`;
  const w = window.open('','_blank','width=1200,height=900'); if (!w) { alert('Pop-up blocked — allow pop-ups to print'); return; } w.document.write(html); w.document.close();
}

function sortBatches(batches) {
  // Ongoing first, then Upcoming, then Completed; inside each group the newest batch (latest start date) comes first.
  const rank = s => { const v = String(s || '').trim().toLowerCase(); return (v === 'ongoing' || v === 'active' || v === 'running') ? 0 : (v === 'completed' || v === 'closed') ? 2 : 1; };
  return [...(batches || [])].sort((a, b) => {
    const d = rank(a.status) - rank(b.status);
    if (d) return d;
    const sa = a.start_date || '', sb = b.start_date || '';
    if (sa !== sb) return sa < sb ? 1 : -1;
    return (b.batch_name || '').localeCompare(a.batch_name || '', undefined, { numeric: true });
  });
}

// Sort students by register number the natural way (TM23002 before TM23010).
const byRegNo = (a, b) => String(a.register_number || '').localeCompare(String(b.register_number || ''), undefined, { numeric: true });

// Who may do what with certificate requests (also enforced in the database by cert_exam_permissions.sql)
const CERT_ADMIN_EMAILS = ['ershadarmstech@gmail.com', 'armstechac@gmail.com'];
const lcMail = e => String(e || '').trim().toLowerCase();

export default function Certificates() {
  const { isEditor, canEdit, canView, staff, user } = useAuth();
  const meMail = lcMail(user?.email);
  const isCertAdmin = CERT_ADMIN_EMAILS.includes(meMail);   // only these can edit or delete a certificate request
  const fmtDate = (d) => { if(!d) return '—'; const dt=new Date(d); const mn=['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return dt.getDate().toString().padStart(2,'0')+'-'+mn[dt.getMonth()]+'-'+dt.getFullYear(); };

  const [certs, setCerts]             = useState([]);
  const [distributions, setDist]      = useState([]);
  const [students, setStudents]       = useState([]);
  const [examReqs, setExamReqs]       = useState([]);
  const [batches, setBatches]         = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [mainTab, setMainTab]         = useState('requests');
  const [filterBatch, setFilterBatch]         = useState('');
  const [selectedIds, setSelectedIds]         = useState([]);
  const [selectedDistIds, setSelectedDistIds] = useState([]);
  const [showDistBulkModal, setShowDistBulkModal] = useState(false);
  const [bulkCollectDate, setBulkCollectDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCertReport, setShowCertReport]   = useState(false);
  const [repReqSearch, setRepReqSearch]       = useState('');   // report: find a certificate request number
  const [repReqBatch, setRepReqBatch]         = useState('');
  const [repReqStatus, setRepReqStatus]       = useState('');   // '', 'pending', 'done'
  const [repReqOpen, setRepReqOpen]           = useState(null); // expanded request number
  const [staffList, setStaffList]             = useState([]);
  const [coordinators, setCoordinators]       = useState([]);
  const [pkgNactet, setPkgNactet] = useState({});   // course package name (lower case) -> No. of NACTET certificates from Master Data
  const [filterTab, setFilterTab]             = useState('all');
  const [confirmAction, setConfirmAction]     = useState(null);
  const [bulkAction, setBulkAction]           = useState('');
  const [filterConcerned, setFilterConcerned] = useState('');
  const [search, setSearch]           = useState('');
  const [distSearch, setDistSearch]   = useState('');
  const [distBatch, setDistBatch]     = useState('');
  const [distType, setDistType]       = useState('');
  const [distStatus, setDistStatus]   = useState('all');
  const [showBulkCollected, setShowBulkCollected] = useState(false);
  const [bulkCollectedBatch, setBulkCollectedBatch] = useState('');
  const [bulkSearch, setBulkSearch] = useState('');
  const [bulkCollectedDate, setBulkCollectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [bulkCollectedSaving, setBulkCollectedSaving] = useState(false);
  const [bulkCertTypes, setBulkCertTypes] = useState({nactet:true,ndt:false,experience:false});
  const [bulkBatchStudents, setBulkBatchStudents] = useState([]);
  const [bulkStudentRows, setBulkStudentRows] = useState([]); // per-student collection data
  const [bulkDefaultDate, setBulkDefaultDate] = useState(new Date().toISOString().split('T')[0]);
  const [bulkCertCounts, setBulkCertCounts] = useState({nactet:1,ndt:1,experience:1});
  const [modalBatch, setModalBatch]   = useState('');
  // a certificate request can be created only by the coordinator of that batch (or the certificate admins)
  const allowedBatchNames = isCertAdmin ? null : batches.filter(b => b.coordinator_email && b.coordinator_email === meMail).map(b => b.batch_name);
  const canCreateCert = isCertAdmin || (allowedBatchNames && allowedBatchNames.length > 0);
  const [filteredExamReqs, setFilteredExamReqs] = useState([]);
  const [evalStudents, setEvalStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [existingCertSuffixes, setExistingCertSuffixes] = useState([]);
  const [editGroupItems, setEditGroupItems] = useState([]);

  const [showModal, setShowModal]     = useState(false);
  const [editId, setEditId]           = useState(null);
  const [form, setForm]               = useState(emptyForm);
  const [saving, setSaving]           = useState(false);
  const [viewItem, setViewItem]       = useState(null);

  const [showDistModal, setShowDistModal] = useState(false);
  const [editDistId, setEditDistId]   = useState(null);
  const [distForm, setDistForm]       = useState(emptyDist);
  const [showWAModal, setShowWAModal]   = useState(false);
  const [waMessage, setWAMessage]       = useState('');
  const [waDists, setWaDists]           = useState([]);
  const [distSaving, setDistSaving]   = useState(false);
  const [viewDist, setViewDist]       = useState(null);
  const [distExtra, setDistExtra]     = useState(false);   // quantity / ID-proof columns available
  const [logEnabled, setLogEnabled]   = useState(false);   // certificate_status_log table available
  const [history, setHistory]         = useState([]);
  const [distHistory, setDistHistory] = useState([]);

  // Only open a tab the user may see
  useEffect(() => {
    const r = canView('cert_requests'), d = canView('cert_distribution');
    if (mainTab === 'requests' && !r && d) setMainTab('distribution');
    if (mainTab === 'distribution' && !d && r) setMainTab('requests');
  }, [mainTab]);

  // Detect optional database features (see the SQL supplied with this version)
  useEffect(() => {
    supabase.from('certificate_distributions').select('quantity, quantity_collected, id_proof_type, id_proof_number').limit(1)
      .then(({ error }) => setDistExtra(!error));
    supabase.from('certificate_status_log').select('id').limit(1)
      .then(({ error }) => setLogEnabled(!error));
  }, []);

  // Load data on mount
  useEffect(() => {
    loadAll().then(() => setInitialLoading(false));


  }, []);

  // Real-time updates (debounced)
  useEffect(() => {
    let _rt = null;
    const _reload = () => { clearTimeout(_rt); _rt = setTimeout(() => { try { loadAll(); } catch(e){} }, 800); };
    const ch = supabase.channel('certificates-realtime')
      .on('postgres_changes', {event:'*', schema:'public', table:'certificate_requests'}, () => _reload())
      .on('postgres_changes', {event:'*', schema:'public', table:'certificate_distributions'}, () => _reload())
      .subscribe();
    return () => { clearTimeout(_rt); supabase.removeChannel(ch); };
  }, []);

  const loadCerts = useCallback(async () => {
    const { data, error } = await fetchAllRows(() => supabase
      .from('certificate_requests')
      .select('*, students(full_name, register_number, batch_name, course_name, contact_number)')
      .order('request_date', { ascending: false }).order('id'));
    if (!error) setCerts(data || []);
    return data || [];
  }, []);

  const loadDists = useCallback(async () => {
    const { data, error } = await fetchAllRows(() => supabase
      .from('certificate_distributions')
      .select('*, students(full_name, register_number, batch_name, course_name, contact_number, whatsapp_number)')
      .order('created_at', { ascending: false }).order('id'));
    if (!error) setDist(data || []);
  }, []);

  async function bulkMarkBatchCollected() {
    if (!bulkCollectedBatch) { toast.error('Select a batch'); return; }
    const batchStudents = bulkStudentRows.filter(r => r.collect);
    if (!batchStudents.length) { toast.error('No students selected'); return; }
    setBulkCollectedSaving(true);
    const TYPES = [
      { exp: 'nactet', col: 'col_nactet', type: 'NACTET Certificate',    legacy: ['NACTET+NDT', 'Certificate'] },
      { exp: 'ndt',    col: 'col_ndt',    type: 'NDT Certificate',       legacy: ['NACTET+NDT'] },
      { exp: 'exp',    col: 'col_exp',    type: 'Experience Certificate', legacy: [] },
    ];
    const studentIds = batchStudents.map(s => s.id);
    const { data: existingRecs, error: exErr } = await supabase.from('certificate_distributions')
      .select('id, student_id, cert_type').in('student_id', studentIds);
    if (exErr) { toast.error('Could not load existing records: ' + exErr.message); setBulkCollectedSaving(false); return; }

    let created = 0, updated = 0, failed = 0, skipped = 0, nothing = 0;
    const toInsert = [];
    for (const s of batchStudents) {
      const mine = (existingRecs || []).filter(r => r.student_id === s.id);
      const used = new Set();
      let touched = false;
      for (const t of TYPES) {
        const expected = Number(s[t.exp] || 0);
        const got = Number(s[t.col] || 0);
        if (got <= 0) continue;                        // nothing collected for this type: leave records alone
        touched = true;
        const full = expected > 0 ? got >= expected : true;
        const note = (s.remarks && s.remarks.trim()) || `Bulk - ${bulkCollectedBatch}`;
        const remarks = `${note} | ${t.type}: ${got}${expected > 0 ? '/' + expected : ''} collected${full ? '' : ' (Partial)'}`;
        // update only the matching record for THIS certificate type (never every record of the student)
        const match = mine.find(r => !used.has(r.id) && r.cert_type === t.type);
        const legacy = mine.find(r => t.legacy.includes(r.cert_type));
        if (match) {
          used.add(match.id);
          const { error } = await supabase.from('certificate_distributions').update({
            collected_by_student: full, student_collection_date: s.date, collector_name: s.full_name, remarks,
            ...(distExtra ? { quantity: expected || got, quantity_collected: got } : {}),
          }).eq('id', match.id);
          if (error) failed++; else updated++;
        } else if (legacy) {
          // older bulk run saved one combined record; update it once instead of creating duplicates
          if (used.has(legacy.id)) { skipped++; continue; }
          used.add(legacy.id);
          const { error } = await supabase.from('certificate_distributions').update({
            collected_by_student: full, student_collection_date: s.date, collector_name: s.full_name, remarks,
            ...(distExtra ? { quantity: expected || got, quantity_collected: got } : {}),
          }).eq('id', legacy.id);
          if (error) failed++; else updated++;
        } else {
          toInsert.push({
            student_id: s.id, cert_type: t.type, collected_by_student: full,
            student_collection_date: s.date, collector_name: s.full_name, remarks, issued_by: staff?.id || null,
            ...(distExtra ? { quantity: expected || got, quantity_collected: got } : {}),
          });
        }
      }
      if (!touched) nothing++;
    }
    for (let i = 0; i < toInsert.length; i += 100) {
      const chunk = toInsert.slice(i, i + 100);
      const { error: insErr } = await supabase.from('certificate_distributions').insert(chunk);
      if (insErr) { failed += chunk.length; toast.error('Insert error: ' + insErr.message); } else created += chunk.length;
    }
    const msg = `Created ${created}, updated ${updated}` + (skipped ? `, ${skipped} covered by older combined record` : '') + (nothing ? `, ${nothing} student(s) had 0 collected` : '') + (failed ? `, ${failed} failed` : '');
    if (failed) toast.error(msg); else toast.success(msg);
    await logEvent({ action: 'bulk collection', note: `Batch ${bulkCollectedBatch}: ${msg}` });
    setBulkCollectedSaving(false);
    if (!failed) { setShowBulkCollected(false); setBulkStudentRows([]); setBulkBatchStudents([]); setBulkCollectedBatch(''); }
    loadAll();
  }

async function loadAll() {

    const [examRes, batchRes, staffRes, certsData, distsData, stuData] = await Promise.all([
      fetchAllRows(() => supabase.from('exam_requests').select('id, request_number, unique_exam_date_from, unique_exam_date_to, exam_type, course_complete_date, attempt_suffix, exam_request_students(student_id)').order('request_number').order('id')),
      supabase.from('batches').select('id, batch_name, status, start_date, handling_staff_id').order('batch_name'),
      supabase.from('staff').select('id, full_name, email').eq('is_active', true),
      fetchAllRows(() => supabase.from('certificate_requests').select('*, students(full_name, register_number, batch_name, course_name, course_package_id, contact_number)').order('request_date',{ascending:false}).order('id')),
      fetchAllRows(() => supabase.from('certificate_distributions').select('*, students(full_name, register_number, batch_name, course_name, contact_number, whatsapp_number)').order('created_at',{ascending:false}).order('id')),
      getAllStudents(),
    ]);
    setExamReqs(examRes.data || []);
    { const em = {}; (staffRes.data || []).forEach(x => { em[x.id] = lcMail(x.email); });
      setBatches(sortBatches((batchRes.data || []).map(b => ({ ...b, coordinator_email: em[b.handling_staff_id] || '' })))); }
    setStaffList(staffRes.data || []);
    // Load coordinators separately
    supabase.from('hr_staff')
      .select('id, staff_name, designation')
      .in('designation', ['Office Admin', 'Academic Counselor', 'Coordinator-Marketing'])
      .eq('is_active', true)
      .order('staff_name')
      .then(({ data }) => setCoordinators(data || []));
    setCerts(certsData.data || []);
    setDist(distsData.data || []);
    setStudents(Array.isArray(stuData) ? stuData : (stuData?.data || []));
    try {
      const { data: pk } = await supabase.from('course_packages').select('id, course_name, num_nactet_certificates').limit(2000);
      const pm = {}; (pk || []).forEach(p => { if (p.id != null) pm['id:' + p.id] = Number(p.num_nactet_certificates) || 0; if (p.course_name) pm[String(p.course_name).trim().toLowerCase()] = Number(p.num_nactet_certificates) || 0; });
      setPkgNactet(pm);
    } catch (e) { /* the typed request total is used instead */ }
  }

  // When batch selected in modal — filter exam requests for that batch
  async function handleModalBatchChange(batchName) {
    setModalBatch(batchName);
    setSelectedStudentIds([]);
    setForm(f => ({ ...f, exam_request_id: '', student_id: '' }));
    setEvalStudents([]);
    if (!batchName) { setFilteredExamReqs([]); return; }
    // First try from students state
    const batchStudentIds = students.filter(s => s.batch_name === batchName && s.student_status !== 'Discontinued').map(s => s.id);
    const relevant = examReqs.filter(r => (r.exam_request_students || []).some(ers => batchStudentIds.includes(ers.student_id)));
    if (relevant.length > 0) {
      setFilteredExamReqs(relevant);
    } else {
      // Fetch directly from DB with batch_name join
      const { data } = await fetchAllRows(() => supabase
        .from('exam_requests')
        .select('id, request_number, unique_exam_date_from, unique_exam_date_to, exam_type, course_complete_date, attempt_suffix, exam_request_students(student_id, students(batch_name))')
        .order('request_number').order('id'));
      if (data) {
        const rel = data.filter(r =>
          (r.exam_request_students || []).some(ers => ers.students?.batch_name === batchName)
        );
        setFilteredExamReqs(rel);
      }
    }
  }

  // When exam request selected — load students who have marks entered
  async function handleExamReqChange(examReqId) {
    setSelectedStudentIds([]);   // never carry students over from a different exam request
    setForm(f => ({ ...f, exam_request_id: examReqId, student_id: '' }));
    setEvalStudents([]);
    if (!examReqId) return;

    // Auto-fill dates from exam request
    const req = examReqs.find(r => r.id === examReqId);
    if (req) {
      setForm(f => ({
        ...f,
        exam_request_id: examReqId,
        course_complete_date: req.course_complete_date || '',
        exam_end_date: req.unique_exam_date_to || '',
      }));
    }

    // Get students with marks + their attempt numbers
    const { data: marks, error: marksError } = await supabase
      .from('exam_marks')
      .select('student_id, attempt_number')
      .eq('exam_request_id', examReqId);

    if (marksError) { toast.error('Error loading marks: ' + marksError.message); return; }

    // Get students who ALREADY have a certificate request for this exam request
    const { data: existingCerts } = await supabase
      .from('certificate_requests')
      .select('student_id')
      .eq('exam_request_id', examReqId);

    const alreadyCertifiedIds = new Set((existingCerts || []).map(c => c.student_id));

    if (marks && marks.length > 0) {
      // Build map of student_id -> max attempt_number
      const attemptMap = {};
      marks.forEach(m => {
        if (!attemptMap[m.student_id] || m.attempt_number > attemptMap[m.student_id]) {
          attemptMap[m.student_id] = m.attempt_number;
        }
      });

      const studentIds = Object.keys(attemptMap);
      let evalStds = students.filter(s => studentIds.includes(s.id) && s.student_status !== 'Discontinued');
      if (modalBatch) evalStds = evalStds.filter(s => s.batch_name === modalBatch).sort(byRegNo);
      evalStds = evalStds.filter(s => !alreadyCertifiedIds.has(s.id));
      evalStds = evalStds.map(s => ({ ...s, _attemptNumber: attemptMap[s.id] || 1 }));
      setEvalStudents(evalStds);
    } else {
      setEvalStudents([]);
    }
  }

  // Load existing certificate_requests for the selected exam request,
  // so we can compute the next batch suffix (a, b, c, ...) for this exam request.
  useEffect(() => {
    if (!form.exam_request_id) { setExistingCertSuffixes([]); return; }
    const req = examReqs.find(r => r.id === form.exam_request_id);
    const base = req?.request_number || '';
    supabase.from('certificate_requests').select('request_number, cert_group_number').eq('exam_request_id', form.exam_request_id)
      .then(({ data }) => {
        const sfx = (data || [])
          .map(c => {
            const grp = c.cert_group_number || c.request_number || '';
            return grp.startsWith(base) ? grp.slice(base.length) : null;
          })
          .filter(s => s !== null);
        setExistingCertSuffixes(sfx);
      });
  }, [form.exam_request_id, examReqs]);

  // Compute the certificate request number for the current selection.
  // - If this exam request has NO certificate requests yet AND all of its
  //   students are being selected now -> use the exam request number as-is.
  // - Otherwise -> exam request number + next available letter suffix (a, b, c, ...)
  //   based on how many batches have already been created for this exam request.
  function getCertReqNumber(examReqId, selectedCount) {
    const req = examReqs.find(r => r.id === examReqId);
    if (!req) {
      const d = new Date();
      const ymd = String(d.getFullYear()).slice(2) + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      return `${REQ_NO_PREFIX[form.cert_type] || 'CERT'}-${ymd}-${String(Date.now()).slice(-4)}`;
    }
    const base = req.request_number;
    const total = (req.exam_request_students || []).length;
    if (existingCertSuffixes.length === 0 && selectedCount === total && total > 0) {
      return base;
    }
    const letters = 'abcdefghijklmnopqrstuvwxyz';
    const usedLetters = new Set(existingCertSuffixes.filter(s => /^[a-z]$/.test(s)));
    for (const L of letters) {
      if (!usedLetters.has(L)) return base + L;
    }
    return base + 'z2'; // fallback if more than 26 batches
  }

  // ── SAVE CERTIFICATE REQUEST ──
  async function saveRequest() {
    if (selectedStudentIds.length === 0 && !form.student_id) { toast.error('Select at least one student'); return; }
    if (editId && !isCertAdmin) { toast.error('Only ' + CERT_ADMIN_EMAILS.join(' and ') + ' can edit a certificate request'); return; }
    if (!editId && allowedBatchNames) {
      const ids = selectedStudentIds.length > 0 ? selectedStudentIds : [form.student_id];
      const bad = students.filter(s => ids.includes(s.id) && !allowedBatchNames.includes(s.batch_name));
      if (bad.length) { toast.error('You can create certificate requests only for the batches you coordinate (' + bad[0].batch_name + ' is not yours)'); return; }
    }
    setSaving(true);

    const basePayload = {
      request_date:            form.request_date || null,
      issue_date:              form.issue_date || null,
      course_complete_date:    form.course_complete_date || null,
      exam_end_date:           form.exam_end_date || null,
      cert_issue_date:         form.cert_issue_date || null,
      purpose:                 form.purpose || null,
      processing_status:       form.processing_status || 'submitted',
      remarks:                 form.remarks || null,
      total_nactet_certs:      form.total_nactet_certs ? Number(form.total_nactet_certs) : null,
      total_ndt_certs:         form.total_ndt_certs ? Number(form.total_ndt_certs) : null,
      experience_cert_created: form.experience_cert_created || 'no',
      concerned_person:        form.concerned_person || null,
      batch_coordinator_id:    form.batch_coordinator_id || null,
      dispatch_mode:           form.dispatch_mode || 'collect',
      courier_name:            form.courier_name || null,
      tracking_number:         form.tracking_number || null,
      dispatch_date:           form.dispatch_date || null,
      cert_status:             form.cert_status || 'active',
      cert_status_notes:       form.cert_status_notes || null,
    };
    if (form.exam_request_id) basePayload.exam_request_id = form.exam_request_id;
    // cert_type is only sent when chosen (or already present on the record) so older databases keep working
    if (form.cert_type || (editId && 'cert_type' in (editGroupItems[0] || {}))) basePayload.cert_type = form.cert_type || null;
    const insertCertRow = async (payload) => {
      const res = await supabase.from('certificate_requests').insert(payload);
      if (res.error && /cert_type/i.test(res.error.message)) {
        return { error: { message: "Certificate type column is missing. Run in Supabase SQL: alter table certificate_requests add column if not exists cert_type text;" } };
      }
      return res;
    };

    // Multi-student: create one record per student, all sharing the same request number
    if (!editId) {
      const studentsToSave = selectedStudentIds.length > 0 ? selectedStudentIds : [form.student_id];
      const sharedRequestNumber = getCertReqNumber(form.exam_request_id, studentsToSave.length);
      let successCount = 0;
      const failures = [];
      for (const sid of studentsToSave) {
        const payload = { ...basePayload, student_id: sid, cert_group_number: sharedRequestNumber, request_number: `${sharedRequestNumber}-${sid.slice(0,8)}` };
        const { error } = await insertCertRow(payload);
        if (!error) successCount++; else failures.push(error.message);
      }
      if (successCount === 0) { toast.error('Nothing was saved: ' + (failures[0] || 'unknown error')); setSaving(false); return; }   // keep the form open
      if (failures.length > 0) toast.error(`${failures.length} of ${studentsToSave.length} failed: ${failures[0]}`);
      toast.success(`Created ${successCount} certificate request(s) — ${sharedRequestNumber}`);
      setEditId(null); setForm(emptyForm); setModalBatch(''); setFilteredExamReqs([]); setEvalStudents([]); setSelectedStudentIds([]); setShowModal(false);
      loadCerts(); setSaving(false); return;
    }

    // Edit mode: sync the group's students (add / update / remove) while keeping the same request_number
    const studentsToSave = selectedStudentIds.length > 0 ? selectedStudentIds : [form.student_id];
    const requestNumber = editGroupItems[0]?.cert_group_number || editGroupItems[0]?.request_number;
    const originalIds = editGroupItems.map(x => x.student_id);

    const toRemove = editGroupItems.filter(x => !studentsToSave.includes(x.student_id));
    const toUpdate = editGroupItems.filter(x => studentsToSave.includes(x.student_id));
    const toAdd = studentsToSave.filter(sid => !originalIds.includes(sid));

    const errors = [];
    if (toRemove.length > 0) {
      const ids = toRemove.map(x => x.id);
      await supabase.from('certificate_distributions').delete().in('certificate_request_id', ids);
      const { error } = await supabase.from('certificate_requests').delete().in('id', ids);
      if (error) errors.push('remove: ' + error.message);
    }
    const newStatus = form.processing_status || 'submitted';
    const movingBack = toUpdate.some(x => x.processing_status === 'collected' && newStatus !== 'collected');
    if (movingBack && !window.confirm('Moving this request back from At Office will remove its distribution record(s) that the student has not collected yet. Continue?')) { setSaving(false); return; }
    for (const item of toUpdate) {
      const { processing_status: _ps, ...restPayload } = basePayload;
      const { error } = await supabase.from('certificate_requests').update({ ...restPayload, student_id: item.student_id }).eq('id', item.id);
      if (error) { errors.push('update: ' + error.message); continue; }
      // a changed status goes through updateStatus so it does exactly what the front-page buttons do
      if ((item.processing_status || 'submitted') !== newStatus) {
        const r = await updateStatus(item.id, newStatus, { silent: true });
        if (!r || !r.ok) errors.push('status: ' + ((r && r.error) || 'failed'));
      }
    }
    for (const sid of toAdd) {
      const { error } = await insertCertRow({ ...basePayload, student_id: sid, cert_group_number: requestNumber, request_number: `${requestNumber}-${sid.slice(0,8)}` });
      if (error) errors.push('add: ' + error.message);
    }

    if (errors.length > 0) {
      toast.error('Some changes failed: ' + errors.join(' | '));
      setSaving(false);
      return;
    }
    toast.success('Updated!');
    setShowModal(false); setEditId(null); setForm(emptyForm); setSelectedStudentIds([]); setEditGroupItems([]);
    setSaving(false);
    await loadCerts();
    setFilterTab('all'); setMainTab('requests');
  }

  // Change history (silently skipped when the log table does not exist)
  async function logEvent({ requestId = null, distId = null, action, from = null, to = null, note = null }) {
    if (!logEnabled) return;
    try {
      await supabase.from('certificate_status_log').insert({
        certificate_request_id: requestId, distribution_id: distId, action,
        from_status: from, to_status: to, note,
        changed_by: staff?.id || null, changed_by_name: staff?.full_name || staff?.email || null,
      });
    } catch (e) { /* history is best-effort */ }
  }
  useEffect(() => {
    if (!logEnabled || !viewItem) { setHistory([]); return; }
    const gk = viewItem.cert_group_number || viewItem.request_number;
    const ids = certs.filter(c => (c.cert_group_number || c.request_number) === gk).map(c => c.id);
    supabase.from('certificate_status_log').select('*').in('certificate_request_id', ids.length ? ids : [viewItem.id])
      .order('created_at', { ascending: false }).limit(60).then(({ data }) => setHistory(data || []));
  }, [viewItem, logEnabled]);
  useEffect(() => {
    if (!logEnabled || !viewDist) { setDistHistory([]); return; }
    supabase.from('certificate_status_log').select('*').eq('distribution_id', viewDist.id)
      .order('created_at', { ascending: false }).limit(60).then(({ data }) => setDistHistory(data || []));
  }, [viewDist, logEnabled]);
  const renderHistory = (rows) => rows.length === 0 ? null : (
    <div className="detail-row" style={{ alignItems: 'flex-start' }}>
      <div className="detail-label">History</div>
      <div className="detail-value">
        {rows.map(h => (
          <div key={h.id} style={{ padding: '3px 0', borderBottom: '1px solid var(--border)', fontSize: 12 }}>
            <span style={{ fontWeight: 600 }}>{h.action}{h.to_status ? ` → ${statusLabel(h.to_status)}` : ''}</span>
            {h.note ? <span style={{ color: 'var(--text2)' }}> — {h.note}</span> : null}
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>{h.changed_by_name || '—'} · {new Date(h.created_at).toLocaleString('en-IN')}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // Re-issue a returned / lost certificate: new request group (number + "R1", "R2" ...) copied from the old one
  async function reissueGroup(group) {
    const items = group.items;
    const baseNo = group.request_number;
    if (allowedBatchNames && items.some(it => !allowedBatchNames.includes(it.students?.batch_name))) { toast.error('You can create certificate requests only for the batches you coordinate'); return; }
    if (!window.confirm(`Create a re-issue request for ${baseNo} (${items.length} student${items.length > 1 ? 's' : ''})?`)) return;
    const existing = certs.map(c => c.cert_group_number || c.request_number).filter(n => n && n.startsWith(baseNo + 'R'));
    const newNo = `${baseNo}R${new Set(existing).size + 1}`;
    const today = new Date().toISOString().split('T')[0];
    let ok = 0; const errs = [];
    for (const it of items) {
      const rec = {
        student_id: it.student_id, exam_request_id: it.exam_request_id || null,
        request_date: today, course_complete_date: it.course_complete_date || null, exam_end_date: it.exam_end_date || null,
        total_nactet_certs: it.total_nactet_certs ?? null, total_ndt_certs: it.total_ndt_certs ?? null,
        experience_cert_created: it.experience_cert_created || 'no', concerned_person: it.concerned_person || null,
        batch_coordinator_id: it.batch_coordinator_id || null, purpose: it.purpose || null,
        processing_status: 'submitted', dispatch_mode: it.dispatch_mode || 'collect', cert_status: 'active',
        remarks: `Re-issue of ${baseNo} (${it.cert_status || 'returned'}${it.cert_status_notes ? ': ' + it.cert_status_notes : ''})`,
        cert_group_number: newNo, request_number: `${newNo}-${it.student_id.slice(0, 8)}`,
      };
      if ('cert_type' in it) rec.cert_type = it.cert_type || null;
      const { error } = await supabase.from('certificate_requests').insert(rec);
      if (error) errs.push(error.message); else ok++;
    }
    if (ok > 0) {
      await logEvent({ requestId: items[0].id, action: 're-issue', note: `New request ${newNo} created from ${baseNo}` });
      toast.success(`Re-issue request ${newNo} created (${ok} student${ok > 1 ? 's' : ''})`);
      loadCerts();
    }
    if (errs.length) toast.error(`${errs.length} failed: ${errs[0]}`);
  }

  // Delete an entire certificate request group (all students sharing the same request_number)
  async function deleteGroup(ids, num) {
    if (!isCertAdmin) { toast.error('Only ' + CERT_ADMIN_EMAILS.join(' and ') + ' can delete a certificate request'); return; }
    const n = ids.length;
    if (!window.confirm(`Delete certificate request ${num} (${n} student${n > 1 ? 's' : ''})? This will also delete related distributions.`)) return;
    await supabase.from('certificate_distributions').delete().in('certificate_request_id', ids);
    const { error } = await supabase.from('certificate_requests').delete().in('id', ids);
    if (error) toast.error(error.message);
    else { await logEvent({ requestId: ids[0], action: 'deleted', note: `Request ${num} (${n} student${n > 1 ? 's' : ''}) deleted` }); toast.success('Deleted'); loadCerts(); }
  }

  // ── UPDATE STATUS ──
  // 'collected' is the stored value for "At Office" (certificate received at our office).
  // Moving a request to At Office creates the distribution record(s) so the student can collect them.
  async function updateStatus(id, status, opts = {}) {
    const silent = !!opts.silent;
    const payload = { processing_status: status };
    if (status === 'PROCESSED') {
      payload.certificate_received_date = new Date().toISOString().split('T')[0];
      // Alert to create an experience certificate
      const { data: certData } = await supabase.from('certificate_requests')
        .select('students(full_name, register_number), experience_cert_created')
        .eq('id', id).single();
      if (certData?.experience_cert_created !== 'yes') {
        await supabase.from('notifications').insert({
          title: '📄 Experience Certificate Needed',
          message: `Certificate request for ${certData?.students?.full_name} (${certData?.students?.register_number}) has been processed. Please create an Experience Certificate.`,
          type: 'reminder',
          link: '/certificates',
          is_global: true,   // notifications has no staff-recipient column; global = visible to all staff
          created_by: staff?.id,
        });
      }
    }
    if (status === 'collected') payload.collected_by_student = true;
    const prevRow = certs.find(c => c.id === id);
    if (prevRow && prevRow.processing_status === 'collected' && status !== 'collected') {
      payload.collected_by_student = false;
      await supabase.from('certificate_distributions').delete().eq('certificate_request_id', id).eq('collected_by_student', false);
    }
    const { error } = await supabase.from('certificate_requests').update(payload).eq('id', id);
    if (error) { if (!silent) toast.error(error.message); return { ok: false, error: error.message }; }
    await logEvent({ requestId: id, action: 'status', from: prevRow?.processing_status || null, to: status, note: prevRow?.students?.full_name || null });

    let created = 0;
    if (status === 'collected') {
      const { data: certReq } = await supabase.from('certificate_requests').select('*').eq('id', id).single();
      if (certReq) {
        const types = [];
        if (certReq.cert_type && REQ_TO_DIST_TYPE[certReq.cert_type]) {
          types.push(REQ_TO_DIST_TYPE[certReq.cert_type]);
        } else {
          if (Number(certReq.total_nactet_certs) > 0 || !(Number(certReq.total_ndt_certs) > 0)) types.push('NACTET Certificate');
          if (Number(certReq.total_ndt_certs) > 0) types.push('NDT Certificate');
        }
        for (const t of types) {
          // skip if an uncollected record of the same type already exists for this student
          const { data: dup } = await supabase.from('certificate_distributions').select('id')
            .eq('student_id', certReq.student_id).eq('cert_type', t).eq('collected_by_student', false).limit(1);
          if (dup && dup.length > 0) continue;
          const rec = {
            student_id:             certReq.student_id,
            cert_type:              t,
            request_date:           certReq.request_date || null,
            issue_date:             certReq.cert_issue_date || certReq.issue_date || new Date().toISOString().split('T')[0],
            collected_by_student:   false,
            remarks:                `Certificate at Armstech office — ready for student collection — from request ${certReq.cert_group_number || certReq.request_number || ''}`,
            issued_by:              staff?.id || null,
            certificate_request_id: id,
          };
          let { error: e2 } = await supabase.from('certificate_distributions').insert(rec);
          if (e2) {   // retry without the link column in case it does not exist in this database
            const { certificate_request_id, ...rest } = rec;
            ({ error: e2 } = await supabase.from('certificate_distributions').insert(rest));
          }
          if (e2) { if (!silent) toast.error('Distribution record failed: ' + e2.message); return { ok: true, created, warn: e2.message }; }
          created++;
        }
      }
    }
    if (!silent) {
      toast.success(status === 'collected'
        ? `At Office ✅ — ${created} distribution record(s) created, ready for student collection`
        : `Status updated to ${statusLabel(status)} ✅`);
      loadCerts();
      if (status === 'collected') loadDists();
    }
    return { ok: true, created };
  }

  // Apply a status change to many requests: only valid transitions, one summary message, one reload
  async function applyStatusChange(ids, status) {
    const from = status === 'PROCESSED' ? 'submitted' : 'PROCESSED';
    const eligible = ids.filter(id => certs.find(c => c.id === id)?.processing_status === from);
    if (eligible.length === 0) { toast.error(`Only ${statusLabel(from)} requests can be moved to ${statusLabel(status)}`); return; }
    let ok = 0, failed = 0, created = 0;
    for (const id of eligible) {
      const r = await updateStatus(id, status, { silent: true });
      if (r?.ok) { ok++; created += r.created || 0; } else failed++;
    }
    const skipped = ids.length - eligible.length;
    await loadCerts();
    if (status === 'collected') await loadDists();
    const parts = [`${ok} marked ${statusLabel(status)}`];
    if (created) parts.push(`${created} distribution record(s) created`);
    if (skipped) parts.push(`${skipped} skipped (not ${statusLabel(from)})`);
    if (failed) toast.error(`${failed} failed — ${parts.join(', ')}`); else toast.success(parts.join(', '));
  }

  // ── SAVE DISTRIBUTION ──
  async function saveDist() {
    if (!distForm.student_id) { toast.error('Select a student'); return; }
    if (!distForm.cert_type) { toast.error('Select a certificate type'); return; }
    setDistSaving(true);
    const isCollected = !!distForm.collected_by_student;
    const payload = {
      student_id:              distForm.student_id,
      cert_type:               distForm.cert_type,
      request_date:            distForm.request_date || null,
      issue_date:              distForm.issue_date || null,
      issuer_name:             distForm.issuer_name || null,
      collector_name:          distForm.collector_name || null,
      collector_contact:       distForm.collector_contact || null,
      collected_by_student:    isCollected,
      student_collection_date: isCollected ? (distForm.student_collection_date || new Date().toISOString().split('T')[0]) : null,
      remarks:                 distForm.remarks || null,
      issued_by:               staff?.id || null,
    };
    if (distExtra) {
      const qty = distForm.quantity === '' ? null : Number(distForm.quantity);
      const got = distForm.quantity_collected === '' ? null : Number(distForm.quantity_collected);
      payload.quantity = qty;
      payload.quantity_collected = got;
      payload.id_proof_type = distForm.id_proof_type || null;
      payload.id_proof_number = distForm.id_proof_number || null;
      // fewer collected than issued = partial: keep as pending
      if (qty && got !== null && got < qty) { payload.collected_by_student = false; }
      if (qty && got !== null && got >= qty) { payload.collected_by_student = true; }
      if (payload.collected_by_student && !payload.student_collection_date) payload.student_collection_date = new Date().toISOString().split('T')[0];
    }
    const wasCollected = editDistId ? !!distributions.find(x => x.id === editDistId)?.collected_by_student : false;
    let savedId = editDistId;
    let error;
    if (editDistId) {
      ({ error } = await supabase.from('certificate_distributions').update(payload).eq('id', editDistId));
    } else {
      const res = await supabase.from('certificate_distributions').insert(payload).select('id').single();
      error = res.error; savedId = res.data?.id || null;
    }
    if (error) { toast.error('Error: ' + error.message); setDistSaving(false); return; }
    await logEvent({ distId: savedId, action: editDistId ? (payload.collected_by_student && !wasCollected ? 'collected' : 'edited') : 'created', to: payload.collected_by_student ? 'collected' : 'pending', note: `${payload.cert_type}${payload.collector_name ? ' — ' + payload.collector_name : ''}${payload.id_proof_type ? ' — ID: ' + payload.id_proof_type : ''}` });
    toast.success(editDistId ? 'Updated!' : 'Distribution recorded!');
    setShowDistModal(false);
    setEditDistId(null);
    setDistForm(emptyDist);
    setDistSaving(false);
    loadDists();
  }

  async function deleteDist(id) {
    if (!window.confirm('Delete this distribution record?')) return;
    const gone = distributions.find(x => x.id === id);
    const { error } = await supabase.from('certificate_distributions').delete().eq('id', id);
    if (error) toast.error(error.message);
    else { await logEvent({ distId: id, action: 'deleted', note: `${gone?.cert_type || ''} — ${gone?.students?.full_name || ''}` }); toast.success('Deleted'); loadDists(); }
  }

  async function openEditReq(group) {
    if (!isCertAdmin) { toast.error('Only ' + CERT_ADMIN_EMAILS.join(' and ') + ' can edit a certificate request'); return; }
    let items = group.items || [group];
    // statusSyncDone: re-read the live status so a change made on the front page is always shown here
    try {
      const { data: fr } = await supabase.from('certificate_requests').select('id, processing_status, cert_status, cert_status_notes, certificate_received_date, collected_by_student').in('id', items.map(x => x.id));
      if (fr && fr.length) { const fm = new Map(fr.map(r => [r.id, r])); items = items.map(x => ({ ...x, ...(fm.get(x.id) || {}) })); }
    } catch (e) { /* fall back to the list data */ }
    const c = items[0];
    setEditId(c.id);
    setEditGroupItems(items);
    setSelectedStudentIds(items.map(x => x.student_id));

    // Pre-fill batch from the joined student record
    const batchName = c.students?.batch_name || '';
    setModalBatch(batchName);

    // Pre-fill filtered exam requests for this batch
    if (batchName) {
      const { data } = await fetchAllRows(() => supabase
        .from('exam_requests')
        .select('id, request_number, unique_exam_date_from, unique_exam_date_to, exam_type, course_complete_date, attempt_suffix, batch_coordinator_id, exam_request_students(student_id, students(batch_name))')
        .order('request_number').order('id'));
      const rel = (data || []).filter(r => (r.exam_request_students || []).some(ers => ers.students?.batch_name === batchName));
      setFilteredExamReqs(rel);
    } else {
      setFilteredExamReqs([]);
    }

    // Load students who have marks for this exam request (Step 3 list), so the
    // currently-certified student is shown even though they already have a cert request.
    if (c.exam_request_id) {
      const { data: marks } = await supabase.from('exam_marks').select('student_id, attempt_number').eq('exam_request_id', c.exam_request_id);
      const attemptMap = {};
      (marks || []).forEach(m => {
        if (!attemptMap[m.student_id] || m.attempt_number > attemptMap[m.student_id]) attemptMap[m.student_id] = m.attempt_number;
      });
      const studentIds = Object.keys(attemptMap);
      if (studentIds.length > 0) {
        const { data: stds } = await supabase.from('students')
          .select('id, full_name, register_number, batch_name, course_name')
          .in('id', studentIds);
        const evalStds = (stds || [])
          .map(s => ({ ...s, _attemptNumber: attemptMap[s.id] || 1 }))
          .sort((a, b) => (a.register_number || '').localeCompare(b.register_number || '', undefined, { numeric: true }));
        setEvalStudents(evalStds);
      } else {
        setEvalStudents([]);
      }
    } else {
      setEvalStudents([]);
    }

    setForm({
      cert_type:               c.cert_type || '',
      student_id:              c.student_id || '',
      exam_request_id:         c.exam_request_id || '',
      request_date:            c.request_date || '',
      issue_date:              c.issue_date || '',
      course_complete_date:    c.course_complete_date || '',
      exam_end_date:           c.exam_end_date || '',
      cert_issue_date:         c.cert_issue_date || '',
      purpose:                 c.purpose || '',
      processing_status:       c.processing_status || 'submitted',
      remarks:                 c.remarks || '',
      total_nactet_certs:      c.total_nactet_certs || '',
      total_ndt_certs:         c.total_ndt_certs || '',
      experience_cert_created: c.experience_cert_created || 'no',
      concerned_person:        c.concerned_person || '',
      batch_coordinator_id:    c.batch_coordinator_id || '',
      dispatch_mode:           c.dispatch_mode || 'collect',
      courier_name:            c.courier_name || '',
      tracking_number:         c.tracking_number || '',
      dispatch_date:           c.dispatch_date || '',
      cert_status:             c.cert_status || 'active',
      cert_status_notes:       c.cert_status_notes || '',
    });
    setShowModal(true);
  }

  function openEditDist(d) {
    setEditDistId(d.id);
    // Pre-fill batch from student
    const studentBatchId = students.find(s => s.id === d.student_id)?.batch_id || '';
    setDistForm({
      student_id:             d.student_id || '',
      batch_id:               studentBatchId,
      cert_type:              d.cert_type || '',
      request_date:           d.request_date || '',
      issue_date:             d.issue_date || '',
      issuer_name:            d.issuer_name || '',
      collector_name:         d.collector_name || '',
      collector_contact:      d.collector_contact || '',
      student_collection_date: d.student_collection_date || '',
      collected_by_student:   !!d.collected_by_student,
      quantity:               d.quantity ?? '',
      quantity_collected:     d.quantity_collected ?? '',
      id_proof_type:          d.id_proof_type || '',
      id_proof_number:        d.id_proof_number || '',
      remarks:                d.remarks || '',
    });
    setShowDistModal(true);
  }

  // Marks for a certificate group: only the linked exam request (all attempts), never other exams
  async function fetchGroupMarks(group) {
    const sids = group.items.map(x => x.student_id);
    const erid = group.first?.exam_request_id;
    let q = supabase.from('exam_marks').select('*').in('student_id', sids);
    if (erid) q = q.eq('exam_request_id', erid);
    const { data } = await q;
    return data || [];
  }

  // ── Filter certs ──
  // Counts are per request (group), matching the list rows
  const allGroups = (() => {
    const m = new Map();
    certs.forEach(c => { const k = c.cert_group_number || c.request_number; if (!m.has(k)) m.set(k, c); });
    return Array.from(m.values());
  })();
  const countGroups = pred => allGroups.filter(pred).length;
  const filtered = certs.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q
      || c.students?.full_name?.toLowerCase().includes(q)
      || c.request_number?.toLowerCase().includes(q)
      || c.cert_group_number?.toLowerCase().includes(q)
      || c.students?.register_number?.toLowerCase().includes(q);
    const matchTab =
      filterTab === 'all' ? true :
      filterTab === 'collected' ? (c.processing_status === 'collected') :
      c.processing_status === filterTab;
    const matchBatch     = !filterBatch     || (c.students?.batches?.batch_name || c.students?.batch_name) === filterBatch;
    const matchConcerned = !filterConcerned || c.concerned_person === filterConcerned;
    return matchSearch && matchTab && matchBatch && matchConcerned;
  });

  // Group certificate requests by request_number so a request with many
  // students shows as a single row (like exam requests).
  const groupedCerts = (() => {
    const map = new Map();
    filtered.forEach(c => {
      const key = c.cert_group_number || c.request_number;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    });
    return Array.from(map.entries()).map(([request_number, items]) => ({
      request_number,
      items,
      first: items[0],
    })).sort((a, b) => (b.first.request_date || b.first.created_at || '').localeCompare(a.first.request_date || a.first.created_at || ''));
  })();

  async function bulkUpdateStatus(status) {
    if (selectedIds.length === 0) { toast.error('Select at least one certificate'); return; }
    setConfirmAction({ ids: selectedIds, status, label: status === 'PROCESSED' ? 'Process' : 'At Office', tab: status });
  }

  async function bulkMarkCollected() {
    if (selectedDistIds.length === 0) { toast.error('Select at least one'); return; }
    for (const id of selectedDistIds) {
      await supabase.from('certificate_distributions').update({
        collected_by_student: true,
        student_collection_date: bulkCollectDate,
      }).eq('id', id);
    }
    toast.success(`${selectedDistIds.length} certificates marked as collected`);
    setSelectedDistIds([]);
    setShowDistBulkModal(false);
    loadAll();
  }

  const daysPending = d => (!d.collected_by_student && d.issue_date) ? Math.floor((Date.now() - new Date(d.issue_date).getTime()) / 86400000) : 0;
  const filteredDist = distributions.filter(d => {
    const q = distSearch.toLowerCase();
    const matchSearch = !q || d.students?.full_name?.toLowerCase().includes(q) || d.students?.register_number?.toLowerCase().includes(q) || (d.students?.batch_name || '').toLowerCase().includes(q);
    const matchBatch = !distBatch || d.students?.batch_name === distBatch;
    const matchType = !distType || d.cert_type === distType;
    const matchStatus = distStatus === 'all' ? true
      : distStatus === 'collected' ? !!d.collected_by_student
      : distStatus === 'pending' ? !d.collected_by_student
      : distStatus === 'overdue' ? daysPending(d) > 7 : true;
    return matchSearch && matchBatch && matchType && matchStatus;
  });
  // Request No. for a distribution row: the linked request if there is one; otherwise (older rows / rows created by
  // Bulk Mark Completed Batch have no link) the same student's request of the same certificate type — the one with the
  // same request date if possible, else the latest.
  const certsById = new Map(certs.map(c => [c.id, c]));
  const certsByStudent = new Map();
  certs.forEach(c => { if (!certsByStudent.has(c.student_id)) certsByStudent.set(c.student_id, []); certsByStudent.get(c.student_id).push(c); });
  const resolveReqFor = d => {
    let r = d.certificate_request_id ? certsById.get(d.certificate_request_id) : null;
    if (!r) {
      const mine = (certsByStudent.get(d.student_id) || []);
      const sameType = mine.filter(c => !c.cert_type || REQ_TO_DIST_TYPE[c.cert_type] === d.cert_type);
      const pool = sameType.length ? sameType : mine;
      r = pool.find(c => d.request_date && c.request_date === d.request_date)
        || pool.slice().sort((a, b) => String(b.request_date || '').localeCompare(String(a.request_date || '')))[0];
    }
    return r || null;
  };
  const requestNoFor = d => {
    const noOf = r => (r.cert_group_number || r.request_number || '').replace(/-[0-9a-f]{8}$/i, '');
    const r = resolveReqFor(d);
    if (r) return noOf(r) || '—';
    const m = String(d.remarks || '').match(/from request\s+(\S+)/i);
    return m ? m[1] : '—';
  };
  function exportDistributions() {
    if (filteredDist.length === 0) { toast.error('Nothing to export'); return; }
    downloadSheet('Certificate_Distribution.xlsx', filteredDist.map((d, i) => ({
      'S.No': i + 1,
      'Student': d.students?.full_name || '',
      'Register No': d.students?.register_number || '',
      'Batch': d.students?.batch_name || '',
      'Certificate Type': d.cert_type || '',
      'Request No': requestNoFor(d) === '—' ? '' : requestNoFor(d),
      'Request Date': d.request_date || '',
      'Issue Date': d.issue_date || '',
      'Issuer': d.issuer_name || '',
      'Collecting Person': d.collector_name || '',
      'Contact': d.collector_contact || d.students?.contact_number || '',
      'Collected': d.collected_by_student ? 'Yes' : 'No',
      'Collection Date': d.student_collection_date || '',
      'Qty Issued': d.quantity ?? '',
      'Qty Collected': d.quantity_collected ?? '',
      'ID Proof': d.id_proof_type ? `${d.id_proof_type}${d.id_proof_number ? ' ' + d.id_proof_number : ''}` : '',
      'Days Pending': daysPending(d) || '',
      'Remarks': d.remarks || '',
    })), 'Distribution');
  }
  const distTypeOptions = [...new Set([...CERT_DIST_TYPES, ...distributions.map(d => d.cert_type).filter(Boolean)])];
  const reportRows = (() => {
    const names = [...new Set([
      ...certs.map(c => c.students?.batch_name),
      ...distributions.map(d => d.students?.batch_name),
    ].filter(Boolean))].sort();
    return names.map(batch => {
      const bCerts = certs.filter(c => c.students?.batch_name === batch);
      const bDist = distributions.filter(d => d.students?.batch_name === batch);
      return {
        batch,
        requested: bCerts.length,
        processed: bCerts.filter(c => c.processing_status === 'PROCESSED').length,
        atOffice: bCerts.filter(c => c.processing_status === 'collected').length,
        distributed: bDist.length,
        collected: bDist.filter(d => d.collected_by_student).length,
        pending: bDist.filter(d => !d.collected_by_student).length,
        overdue: bDist.filter(d => daysPending(d) > 7).length,
      };
    });
  })();

  // ── Report: distribution status of every student, request number by request number ──
  const distStatusOf = (c, ds) => {
    if (!ds.length) return { key: 'none', label: c.processing_status === 'collected' ? 'At office – not yet distributed' : 'Not distributed', badge: 'badge-gray' };
    const done = ds.filter(x => x.collected_by_student).length;
    if (done === ds.length) return { key: 'collected', label: 'Collected', badge: 'badge-green' };
    const d = ds.find(x => !x.collected_by_student);
    if (done > 0 || (d.quantity && Number(d.quantity_collected) > 0))
      return { key: 'partial', label: done > 0 ? `Partial (${done}/${ds.length} types)` : `Partial (${d.quantity_collected}/${d.quantity})`, badge: 'badge-amber' };
    return { key: 'pending', label: 'Awaiting collection', badge: 'badge-amber' };
  };
  const reqReportRows = (() => {
    // same matching as the Distribution tab's "Request No." column
    const distByReq = new Map();
    distributions.forEach(d => { const r = resolveReqFor(d); if (r) { if (!distByReq.has(r.id)) distByReq.set(r.id, []); distByReq.get(r.id).push(d); } });
    const groups = new Map();
    certs.forEach(c => {
      const k = c.cert_group_number || c.request_number || '—';
      if (!groups.has(k)) groups.set(k, { reqNo: k, batch: c.students?.batches?.batch_name || c.students?.batch_name || '', reqDate: c.request_date || '', certType: c.cert_type || '', items: [] });
      const ds = distByReq.get(c.id) || [];
      const d = ds.find(x => !x.collected_by_student) || ds[0];   // row shown for dates / collector
      groups.get(k).items.push({ c, d, ds, st: distStatusOf(c, ds) });
    });
    return Array.from(groups.values()).map(g => {
      const n = key => g.items.filter(i => i.st.key === key).length;
      return { ...g, total: g.items.length, processed: g.items.filter(i => i.c.processing_status === 'PROCESSED').length,
        atOffice: g.items.filter(i => i.c.processing_status === 'collected').length,
        distributed: g.items.length - n('none'), collected: n('collected'), partial: n('partial'), awaiting: n('pending'), notDist: n('none') };
    }).sort((a, b) => String(b.reqDate).localeCompare(String(a.reqDate)) || String(b.reqNo).localeCompare(String(a.reqNo), undefined, { numeric: true }));
  })();
  const reqReportShown = reqReportRows.filter(g => {
    const q = repReqSearch.trim().toLowerCase();
    if (q && !(String(g.reqNo).toLowerCase().includes(q) || g.items.some(i => (i.c.students?.full_name || '').toLowerCase().includes(q) || (i.c.students?.register_number || '').toLowerCase().includes(q)))) return false;
    if (repReqBatch && g.batch !== repReqBatch) return false;
    if (repReqStatus === 'pending' && g.collected === g.total) return false;
    if (repReqStatus === 'done' && g.collected !== g.total) return false;
    return true;
  });
  function exportReqReport() {
    const out = [];
    reqReportShown.forEach(g => g.items.forEach(({ c, d, st }) => out.push({
      'Request No': g.reqNo, 'Batch': g.batch, 'Request Date': c.request_date || '', 'Certificate': c.cert_type || '',
      'Student': c.students?.full_name || '', 'Register No': c.students?.register_number || '',
      'Request Status': statusLabel(c.processing_status), 'Distribution Status': st.label,
      'Issue Date': d?.issue_date || '', 'Collected On': d?.collected_by_student ? (d.student_collection_date || '') : '',
      'Collected By': d?.collected_by_student ? (d.collector_name || '') : '', 'Days Pending': d ? (daysPending(d) || '') : '',
    })));
    if (!out.length) { toast.error('Nothing to export'); return; }
    downloadSheet('Certificate_Request_Distribution.xlsx', out, 'Distribution by Request');
  }

  if (initialLoading) return <div className="loading"><div className="spinner" /><span>Loading...</span></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div><h1 className="page-title">Certificates</h1><p className="page-subtitle">Manage certificate requests and distribution</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          {canEdit('certificates') && canCreateCert && mainTab === 'requests' && <button className="btn btn-primary" onClick={() => { setEditId(null); setForm(emptyForm); setModalBatch(''); setFilteredExamReqs([]); setEvalStudents([]); setSelectedStudentIds([]); setEditGroupItems([]); setShowModal(true); }}><Plus size={14} /> New Request</button>}
          {canEdit('certificates') && mainTab === 'distribution' && <button className="btn btn-primary" onClick={() => { setEditDistId(null); setDistForm(emptyDist); setShowDistModal(true); }}><Plus size={14} /> Add Distribution</button>}
          {canEdit('certificates') && mainTab === 'distribution' && selectedDistIds.length>0 && (
            <button className="btn btn-sm" style={{background:'var(--green-light)',color:'var(--green)'}}
              onClick={()=>setShowDistBulkModal(true)}>
              ✅ Mark {selectedDistIds.length} as Collected
            </button>
          )}
          <button className={`btn btn-sm ${showCertReport?'btn-primary':'btn-ghost'}`} onClick={()=>setShowCertReport(r=>!r)}>📊 Report</button>

        </div>
      </div>

      {/* Certificate Report */}
      {showCertReport && (
        <div className="card" style={{marginBottom:16}}>
          <div style={{fontWeight:700,fontSize:14,marginBottom:12}}>📊 Certificate Report</div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
            <button className="btn btn-ghost btn-sm" style={{color:'#217346'}} onClick={() => {
              if (reportRows.length === 0) { toast.error('Nothing to export'); return; }
              downloadSheet('Certificate_Report.xlsx', reportRows.map(r => ({ 'Batch': r.batch, 'Requested': r.requested, 'Processed': r.processed, 'At Office': r.atOffice, 'Distributed': r.distributed, 'Collected': r.collected, 'Pending Collection': r.pending, 'Overdue (7d+)': r.overdue })), 'Report');
            }}>📊 Export Excel</button>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
              <thead>
                <tr style={{background:'var(--accent-light)'}}>
                  {['Batch','Requested','Processed','At Office','Distributed','Collected','Pending Collection','Overdue (7d+)'].map(h=>(
                    <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {reportRows.map(r=>(
                  <tr key={r.batch} style={{borderBottom:'1px solid var(--border)'}}>
                    <td style={{padding:'7px 10px',fontWeight:600}}>{r.batch}</td>
                    <td style={{padding:'7px 10px',textAlign:'center'}}>{r.requested}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--green)'}}>{r.processed}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--blue)'}}>{r.atOffice}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--blue)'}}>{r.distributed}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{r.collected}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:r.pending>0?'var(--amber)':'var(--text3)'}}>{r.pending||'—'}</td>
                    <td style={{padding:'7px 10px',textAlign:'center',color:r.overdue>0?'var(--red)':'var(--text3)',fontWeight:r.overdue>0?700:400}}>{r.overdue||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Request-wise distribution status */}
          <div style={{marginTop:22,paddingTop:14,borderTop:'1px solid var(--border)'}}>
            <div style={{fontWeight:700,fontSize:14,marginBottom:10}}>📦 Distribution Status by Request No.</div>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:10}}>
              <input placeholder="Search request no / student…" value={repReqSearch} onChange={e=>setRepReqSearch(e.target.value)} style={{width:240}}/>
              <select value={repReqBatch} onChange={e=>setRepReqBatch(e.target.value)} style={{width:150}}>
                <option value="">All batches</option>
                {[...new Set(reqReportRows.map(g=>g.batch).filter(Boolean))].sort((a,b)=>String(b).localeCompare(String(a),undefined,{numeric:true})).map(b=><option key={b} value={b}>{b}</option>)}
              </select>
              <select value={repReqStatus} onChange={e=>setRepReqStatus(e.target.value)} style={{width:190}}>
                <option value="">All requests</option>
                <option value="pending">Not fully collected</option>
                <option value="done">All collected</option>
              </select>
              <span style={{fontSize:12,color:'var(--text3)'}}>{reqReportShown.length} request{reqReportShown.length===1?'':'s'}</span>
              <button className="btn btn-ghost btn-sm" style={{color:'#217346',marginLeft:'auto'}} onClick={exportReqReport}>📊 Export Excel</button>
            </div>
            <div style={{overflowX:'auto',maxHeight:420,overflowY:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'var(--accent-light)'}}>
                    {['Request No','Batch','Date','Students','Distributed','Collected','Awaiting','Not distributed'].map(h=>(
                      <th key={h} style={{padding:'6px 10px',textAlign:'left',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reqReportShown.length===0 && <tr><td colSpan={8} style={{padding:14,textAlign:'center',color:'var(--text3)'}}>No requests match</td></tr>}
                  {reqReportShown.map(g=>{
                    const open = repReqOpen === g.reqNo;
                    return (
                      <React.Fragment key={g.reqNo}>
                        <tr onClick={()=>setRepReqOpen(open?null:g.reqNo)} style={{borderBottom:'1px solid var(--border)',cursor:'pointer',background:open?'var(--accent-light)':undefined}}>
                          <td style={{padding:'7px 10px',fontWeight:600,whiteSpace:'nowrap'}}>{open?'▾':'▸'} {g.reqNo}</td>
                          <td style={{padding:'7px 10px'}}>{g.batch||'—'}</td>
                          <td style={{padding:'7px 10px',whiteSpace:'nowrap'}}>{g.reqDate?fmtDate(g.reqDate):'—'}</td>
                          <td style={{padding:'7px 10px',textAlign:'center'}}>{g.total}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:'var(--blue)'}}>{g.distributed}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{g.collected}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:(g.awaiting+g.partial)>0?'var(--amber)':'var(--text3)'}}>{(g.awaiting+g.partial)||'—'}</td>
                          <td style={{padding:'7px 10px',textAlign:'center',color:g.notDist>0?'var(--red)':'var(--text3)'}}>{g.notDist||'—'}</td>
                        </tr>
                        {open && (
                          <tr><td colSpan={8} style={{padding:'6px 10px 12px 28px',background:'var(--bg)'}}>
                            <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                              <thead><tr style={{color:'var(--text3)'}}>
                                {['Student','Reg No','Certificate','Request status','Distribution','Issued','Collected on','Collected by','Days pending'].map(h=><th key={h} style={{padding:'4px 8px',textAlign:'left',whiteSpace:'nowrap',fontWeight:600}}>{h}</th>)}
                              </tr></thead>
                              <tbody>
                                {g.items.slice().sort((a,b)=>String(a.c.students?.register_number||'').localeCompare(String(b.c.students?.register_number||''),undefined,{numeric:true})).map(({c,d,st})=>(
                                  <tr key={c.id} style={{borderTop:'1px solid var(--border)'}}>
                                    <td style={{padding:'5px 8px',fontWeight:500}}>{c.students?.full_name||'—'}</td>
                                    <td style={{padding:'5px 8px',fontFamily:'monospace'}}>{c.students?.register_number||'—'}</td>
                                    <td style={{padding:'5px 8px'}}>{c.cert_type||'—'}</td>
                                    <td style={{padding:'5px 8px'}}><span className={`badge ${statusBadge(c.processing_status)}`}>{statusLabel(c.processing_status)}</span></td>
                                    <td style={{padding:'5px 8px'}}><span className={`badge ${st.badge}`}>{st.label}</span></td>
                                    <td style={{padding:'5px 8px',whiteSpace:'nowrap'}}>{d?.issue_date?fmtDate(d.issue_date):'—'}</td>
                                    <td style={{padding:'5px 8px',whiteSpace:'nowrap'}}>{d?.collected_by_student&&d.student_collection_date?fmtDate(d.student_collection_date):'—'}</td>
                                    <td style={{padding:'5px 8px'}}>{d?.collected_by_student?(d.collector_name||'Student'):'—'}</td>
                                    <td style={{padding:'5px 8px',textAlign:'center',color:d&&daysPending(d)>7?'var(--red)':undefined,fontWeight:d&&daysPending(d)>7?700:400}}>{d&&daysPending(d)?daysPending(d):'—'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total',     value: allGroups.length,                                              color: 'var(--accent)', tab: 'all' },
          { label: 'Submitted', value: countGroups(c => c.processing_status === 'submitted'), color: 'var(--amber)', tab: 'submitted' },
          { label: 'Processed', value: countGroups(c => c.processing_status === 'PROCESSED'), color: 'var(--green)', tab: 'PROCESSED' },
          { label: 'Distributed', value: distributions.length,                                    color: 'var(--purple)', tab: null },
          { label: 'At Office',   value: countGroups(c => c.processing_status === 'collected'), color: 'var(--blue)', tab: 'collected' },
          { label: 'Student Collected', value: distributions.filter(d=>d.collected_by_student).length, color: 'var(--green)', tab: null },
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ cursor: s.tab ? 'pointer' : 'default' }}
            onClick={() => { if (s.tab) { setMainTab('requests'); setFilterTab(s.tab); setFilterConcerned(''); setFilterBatch(''); } else setMainTab('distribution'); }}>
            <div className="stat-label">{s.label}</div>
            <div className="stat-value" style={{ color: s.color, fontSize: 24 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Main Tabs */}
      <div className="tabs">
        {canView('cert_requests') && <div className={`tab ${mainTab === 'requests' ? 'active' : ''}`} onClick={() => setMainTab('requests')}>Certificate Requests</div>}
        {canView('cert_distribution') && <div className={`tab ${mainTab === 'distribution' ? 'active' : ''}`} onClick={() => setMainTab('distribution')}>
          Certificate Distribution <span style={{ marginLeft: 5, fontSize: 11, background: 'var(--surface2)', color: 'var(--text3)', padding: '1px 6px', borderRadius: 10 }}>{distributions.length}</span>
        </div>}
        {canAccessOutsider(user, staff) && <div className={`tab ${mainTab === 'outsider' ? 'active' : ''}`} onClick={() => setMainTab('outsider')}>Outsider Requests</div>}
      </div>

      {mainTab === 'outsider' && canAccessOutsider(user, staff) && <OutsiderCertificates />}

      {/* ── REQUESTS TAB ── */}
      {mainTab === 'requests' && canView('cert_requests') && (
        <>
          {/* Filter sub-tabs */}
          <div style={{ display: 'flex', gap: 2, marginBottom: 16, overflowX: 'auto' }}>
            {[
              ['all',       'All',       allGroups.length],
              ['submitted', 'Submitted', countGroups(c => c.processing_status === 'submitted')],
              ['PROCESSED', 'Processed', countGroups(c => c.processing_status === 'PROCESSED')],
              ['collected', 'At Office', countGroups(c => c.processing_status === 'collected')],
            ].map(([v, l, count]) => (
              <button key={v}
                onClick={() => setFilterTab(v)}
                style={{ padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: filterTab === v ? 'var(--accent)' : 'var(--text2)', borderBottom: filterTab === v ? '2px solid var(--accent)' : '2px solid transparent', whiteSpace: 'nowrap', fontFamily: 'var(--font-body)' }}
              >
                {l} <span style={{ fontSize: 11, background: filterTab === v ? 'var(--accent-light)' : 'var(--surface2)', color: filterTab === v ? 'var(--accent)' : 'var(--text3)', padding: '1px 6px', borderRadius: 10 }}>{count}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="search-bar" style={{ minWidth: 200, maxWidth: 280 }}>
              <Search /><input placeholder="Search student, reg no..." value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <SearchableSelect options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))} value={filterBatch} onChange={v => { setFilterBatch(v); setSelectedIds([]); }} placeholder="All Batches" searchPlaceholder="Search batch..." style={{ minWidth: 130, maxWidth: 150 }} />
            <select style={{ width: 120 }} value={filterTab} onChange={e => setFilterTab(e.target.value)}>
              <option value="all">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="PROCESSED">Processed</option>
              <option value="collected">At Office</option>
            </select>
            <select style={{ width: 140 }} value={filterConcerned} onChange={e => setFilterConcerned(e.target.value)}>
              <option value="">Concerned Person</option>
              {coordinators.filter(s => ['Academic Counselor','Office Admin'].includes(s.designation)).map(s => <option key={s.id} value={s.id}>{s.staff_name}</option>)}
            </select>
            {(search || filterBatch || filterConcerned || filterTab !== 'all') && (
              <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setFilterBatch(''); setFilterConcerned(''); setFilterTab('all'); setSelectedIds([]); }}>✕ Clear</button>
            )}
            {/* Bulk actions */}
            {selectedIds.length > 0 && (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>{selectedIds.length} selected</span>
                <button className="btn btn-success btn-sm" onClick={() => bulkUpdateStatus('PROCESSED')}>✓ Process All</button>
                <button className="btn btn-secondary btn-sm" style={{background:'#25D366',color:'#fff',border:'none'}} onClick={()=>{
                  const sel = certs.filter(c => selectedIds.includes(c.id) && c.processing_status === 'collected');
                  if (sel.length === 0) { toast.error('Select requests that are At Office first'); return; }
                  setWaDists(sel);
                  setWAMessage(`Dear [Name], your certificate from Armstech Engineers is ready for collection. Please visit our office at Kochi (EKM) or Thiruvananthapuram (TVM) during working hours (9 AM - 5 PM) to collect it. For queries, contact us. Thank you!`);
                  setShowWAModal(true);
                }}>📱 Notify Students</button>
                <button className="btn btn-primary btn-sm" onClick={() => bulkUpdateStatus('collected')}>✓ Mark Collected</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedIds([])}>✕ Clear</button>
              </div>
            )}
          </div>

          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox"
                      checked={filtered.length > 0 && selectedIds.length === filtered.length}
                      onChange={e => setSelectedIds(e.target.checked ? filtered.map(c => c.id) : [])}
                    />
                  </th>
                  <th>Request No.</th><th>Type</th><th>Students</th><th>Batch</th><th>Request Date</th><th>Course Complete Date</th><th>No. of NACTET Certs</th><th>Status</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedCerts.length === 0 ? (
                  <tr><td colSpan={10}><div className="empty-state"><Award /><h3>No certificates found</h3><p>{search ? 'Try a different search' : filterTab === 'all' ? 'Click "New Request" to add one' : `No ${filterTab} certificates`}</p></div></td></tr>
                ) : groupedCerts.map(group => {
                  const c = group.first;
                  const ids = group.items.map(x => x.id);
                  const allSelected = ids.every(id => selectedIds.includes(id));
                  const allCollected = group.items.every(x => x.collected_by_student);
                  return (
                  <tr key={group.request_number} style={{ background: allSelected ? 'var(--accent-light)' : undefined }}>
                    <td><input type="checkbox" checked={allSelected} onChange={e => setSelectedIds(prev => e.target.checked ? [...new Set([...prev, ...ids])] : prev.filter(id => !ids.includes(id)))} /></td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>{group.request_number}</td>
                    <td style={{ fontSize: 12 }}>{c.cert_type ? certLabel(c.cert_type) : 'NACTET / NDT'}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{group.items.length} student{group.items.length > 1 ? 's' : ''}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                        {group.items.slice(0, 2).map(x => x.students?.full_name).filter(Boolean).join(', ')}
                        {group.items.length > 2 ? ` +${group.items.length - 2} more` : ''}
                      </div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--text3)' }}>{c.students?.batches?.batch_name || c.students?.batch_name || '—'}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(c.request_date)}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(c.course_complete_date)}</td>
                    <td style={{ fontSize: 12, textAlign: 'center' }}>{(() => {
                      // one value per course package: a request with different packages shows each count (e.g. 3 / 6)
                      const m = {};
                      group.items.forEach(x => { const n = certNactetCount(x, x.students, pkgNactet); const k = n || '—'; (m[k] = m[k] || []).push(x.students?.course_name || ''); });
                      const keys = Object.keys(m);
                      return keys.length === 1 ? keys[0] : <span title={keys.map(k => k + ' → ' + [...new Set(m[k])].join(', ')).join('   |   ')}>{keys.join(' / ')}</span>;
                    })()}</td>
                    <td><span className={`badge ${statusBadge(c.processing_status)}`}>{statusLabel(c.processing_status)}</span>
                          {c.processing_status==='PROCESSED'&&(()=>{ const ref=c.certificate_received_date||c.request_date; const days=ref?Math.floor((Date.now()-new Date(ref).getTime())/86400000):0; return days>7?<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,background:'var(--red-light)',color:'var(--red)',fontWeight:700}} title="Processed but not yet marked At Office">⏳ {days}d</span>:null; })()}
                          {c.cert_status==='returned'&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,background:'var(--amber-light)',color:'var(--amber)',fontWeight:700}}>↩ Returned</span>}
                          {c.cert_status==='lost'&&<span style={{marginLeft:4,padding:'1px 6px',borderRadius:8,fontSize:10,background:'var(--red-light)',color:'var(--red)',fontWeight:700}}>❌ Lost</span>}
                          {c.dispatch_mode==='courier'&&c.tracking_number&&<div style={{fontSize:10,color:'var(--blue)',marginTop:2}}>📦 {c.courier_name} · {c.tracking_number}</div>}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewItem(c)}><Eye size={13} /></button>
                        <button className="btn btn-ghost btn-sm" style={{color:'#b45309'}} title="Print Report" onClick={async()=>{
                          const cMarks = await fetchGroupMarks(group);
                          const {data:cTopics} = await supabase.from('topics').select('id,topic_name,short_code');
                          const rows = group.items.map(x => ({ cert: x, student: x.students || {} }));
                          printCertGroupReport(rows, cMarks||[], cTopics||[], coordinators, pkgNactet);
                        }}><Printer size={13}/></button>
                        <button className="btn btn-ghost btn-sm" title="Export Excel" style={{color:'#217346'}} onClick={async()=>{
                          const cMarks2 = await fetchGroupMarks(group);
                          const {data:cTopics2} = await supabase.from('topics').select('id,topic_name,short_code');
                          const rows2 = group.items.map(x => ({ cert: x, student: x.students || {} }));
                          exportCertGroupExcel(rows2, cMarks2||[], cTopics2||[], coordinators, group.batch_name||'export', pkgNactet);
                        }}>📊</button>
                        {canEdit('certificates') && isCertAdmin && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEditReq(group)}><Edit2 size={13} /></button>}
                        {canEdit('certificates') && c.processing_status === 'submitted' && (
                          <button className="btn btn-success btn-sm" onClick={() => setConfirmAction({ ids, status: 'PROCESSED', label: 'Process', tab: 'PROCESSED' })} title="Mark as Processed">
                            ✓ Process
                          </button>
                        )}
                        {canEdit('certificates') && c.processing_status === 'PROCESSED' && !allCollected && (
                          <button className="btn btn-primary btn-sm" onClick={() => setConfirmAction({ ids, status: 'collected', label: 'Collected', tab: 'collected' })} title="Mark as At Office (cert received at our office)" style={{ fontSize: 11 }}>
                            ✓ At Office
                          </button>
                        )}
                        {c.processing_status === 'collected' && (
                          <button className="btn btn-sm" style={{background:'#25D366',color:'#fff',border:'none',fontSize:11}} title="Send WhatsApp notification" onClick={()=>{
                            setWAMessage('Dear [Name], your certificate from Armstech Engineers Pvt Ltd is ready for collection. Please visit our office during working hours (9 AM - 5 PM) to collect it. For queries, contact us. Thank you!');
                            setWaDists(group.items.filter(x=>x.processing_status==='collected').map(x=>({...x, student_id: x.student_id})));
                            setShowWAModal(true);
                          }}>📱 Notify</button>
                        )}
                        {canEdit('certificates') && (c.cert_status === 'returned' || c.cert_status === 'lost') && (
                          <button className="btn btn-ghost btn-sm" style={{ color: 'var(--amber)', fontSize: 11 }} title="Create a re-issue request" onClick={() => reissueGroup(group)}>↻ Re-issue</button>
                        )}
                        {canEdit('certificates') && isCertAdmin && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={() => deleteGroup(ids, group.request_number)}><Trash2 size={13} /></button>}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── DISTRIBUTION TAB ── */}
      {mainTab === 'distribution' && canView('cert_distribution') && (
        <>
          <div style={{marginBottom:16,display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
            <div className="search-bar" style={{flex:1,minWidth:200}}><Search /><input placeholder="Search student or register no..." value={distSearch} onChange={e => setDistSearch(e.target.value)} /></div>
            <select style={{width:130}} value={distBatch} onChange={e=>setDistBatch(e.target.value)}>
              <option value="">All Batches</option>
              {batches.map(b=><option key={b.id||b.batch_name} value={b.batch_name}>{b.batch_name}</option>)}
            </select>
            <select style={{width:170}} value={distType} onChange={e=>setDistType(e.target.value)}>
              <option value="">All Certificate Types</option>
              {distTypeOptions.map(t=><option key={t} value={t}>{t}</option>)}
            </select>
            <select style={{width:140}} value={distStatus} onChange={e=>setDistStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="pending">Pending Collection</option>
              <option value="collected">Collected</option>
              <option value="overdue">Overdue (7d+)</option>
            </select>
            {(distSearch||distBatch||distType||distStatus!=='all')&&<button className="btn btn-ghost btn-sm" onClick={()=>{setDistSearch('');setDistBatch('');setDistType('');setDistStatus('all');}}>✕ Clear</button>}
            <button className="btn btn-ghost btn-sm" style={{color:'#217346'}} onClick={exportDistributions}>📊 Export Excel</button>
            {canEdit('certificates')&&<button className="btn btn-secondary" onClick={()=>setShowBulkCollected(true)}>📦 Bulk Mark Completed Batch</button>}
            {canEdit('certificates')&&selectedDistIds.length>0&&<button className="btn btn-sm" style={{background:'var(--red)',color:'#fff'}} onClick={async()=>{
              if(!window.confirm(`Delete ${selectedDistIds.length} selected records?`))return;
              await supabase.from('certificate_distributions').delete().in('id',selectedDistIds);
              setSelectedDistIds([]);
              toast.success('Deleted');
              loadAll();
            }}>🗑️ Delete {selectedDistIds.length} Selected</button>}
          </div>
          {/* Bulk Collected Modal */}
          {showBulkCollected&&(
            <div className="modal-overlay" style={{zIndex:9999}}>
              <div className="modal" style={{maxWidth:1100,width:'98vw'}}>
                <div className="modal-header">
                  <span className="modal-title">📦 Bulk Certificate Collection — Batch: {bulkCollectedBatch||'(select batch)'}</span>
                  <button className="btn btn-ghost btn-sm" onClick={()=>{setShowBulkCollected(false);setBulkBatchStudents([]);setBulkStudentRows([]);}}><X size={16}/></button>
                </div>
                <div className="modal-body" style={{padding:'12px 16px'}}>
                  {/* Step 1: Select Batch */}
                  <div style={{display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end',marginBottom:12,padding:'10px 12px',background:'var(--surface2)',borderRadius:8}}>
                    <div className="form-group" style={{marginBottom:0,minWidth:160}}>
                      <label style={{fontSize:12}}>Batch *</label>
                      <SearchableSelect
                      options={batches.map(b=>({value:b.batch_name,label:b.batch_name}))}
                      value={bulkCollectedBatch}
                      placeholder='Search batch...'
                      searchPlaceholder='Type to search...'
                      onChange={async b=>{
                        const val=b||'';
                        setBulkCollectedBatch(val); setBulkBatchStudents([]); setBulkStudentRows([]); setBulkSearch('');
                        if(val){
                          const {data:studs}=await supabase.from('students')
                            .select('id,full_name,register_number,course_package_id,course_packages(course_name,num_nactet_certificates,num_ndt_certificates,num_experience_certificates)')
                            .eq('batch_name',val).eq('is_active',true).not('student_status','in','("Discontinued","Shifted")').order('register_number').limit(300);
                          setBulkBatchStudents(studs||[]);
                          const today=new Date().toISOString().split('T')[0];
                          setBulkStudentRows((studs||[]).map(s=>({
                            id:s.id,full_name:s.full_name,register_number:s.register_number,
                            pkg:s.course_packages,
                            nactet:s.course_packages?.num_nactet_certificates||0,
                            ndt:s.course_packages?.num_ndt_certificates||0,
                            exp:s.course_packages?.num_experience_certificates||0,
                            col_nactet:s.course_packages?.num_nactet_certificates||0,
                            col_ndt:s.course_packages?.num_ndt_certificates||0,
                            col_exp:s.course_packages?.num_experience_certificates||0,
                            date:today,collect:true,remarks:''
                          })));
                        }
                      }}
                    />
                    </div>
                    {bulkStudentRows.length>0&&<>
                      <div className="form-group" style={{marginBottom:0}}>
                        <label style={{fontSize:12}}>Default Date</label>
                       <DateInput value={bulkDefaultDate} onChange={e=>setBulkDefaultDate(e.target.value)} style={{fontSize:12}}/>
                      </div>
                      <button className="btn btn-secondary btn-sm" onClick={()=>setBulkStudentRows(r=>r.map(s=>({...s,date:bulkDefaultDate,collect:true,col_nactet:s.nactet,col_ndt:s.ndt,col_exp:s.exp})))}>
                        ✅ Apply to All
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={()=>setBulkStudentRows(r=>r.map(s=>({...s,collect:false})))}>
                        ❌ Clear All
                      </button>
                      <input value={bulkSearch} onChange={e=>setBulkSearch(e.target.value)} placeholder="🔍 Search name / reg no / package..." style={{fontSize:12,padding:'5px 10px',border:'1px solid var(--border)',borderRadius:6,minWidth:260,marginLeft:8}}/>
                      <span style={{fontSize:12,color:'var(--text3)',marginLeft:'auto'}}>{bulkStudentRows.filter(r=>r.collect).length} / {bulkStudentRows.length} marked</span>
                    </>
                    }
                  </div>
                  {/* Student Table */}
                  {bulkStudentRows.length>0&&(
                    <div style={{overflowX:'auto',maxHeight:'55vh',overflowY:'auto',border:'1px solid var(--border)',borderRadius:8}}>
                      <table style={{width:'100%',fontSize:11,borderCollapse:'collapse',minWidth:800}}>
                        <thead><tr style={{background:'var(--surface2)',position:'sticky',top:0,zIndex:1}}>
                          <th style={{padding:'6px 8px'}}></th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Student</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Reg No</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Package</th>
                          <th style={{padding:'6px 8px',textAlign:'center'}} colSpan={3}>Expected</th>
                          <th style={{padding:'6px 8px',textAlign:'center'}} colSpan={3}>Collected</th>
                          <th style={{padding:'6px 8px',textAlign:'center'}}>Date</th>
                          <th style={{padding:'6px 8px',textAlign:'center'}}>Status</th>
                          <th style={{padding:'6px 8px',textAlign:'left'}}>Remarks</th>
                        </tr>
                        <tr style={{background:'var(--surface2)',position:'sticky',top:28,zIndex:1,fontSize:10}}>
                          <th/><th/><th/><th/>
                          <th style={{padding:'2px 4px',textAlign:'center',color:'var(--text3)'}}>NAC</th>
                          <th style={{padding:'2px 4px',textAlign:'center',color:'var(--text3)'}}>NDT</th>
                          <th style={{padding:'2px 4px',textAlign:'center',color:'var(--text3)'}}>Exp</th>
                          <th style={{padding:'2px 4px',textAlign:'center',color:'var(--accent)'}}>NAC</th>
                          <th style={{padding:'2px 4px',textAlign:'center',color:'var(--accent)'}}>NDT</th>
                          <th style={{padding:'2px 4px',textAlign:'center',color:'var(--accent)'}}>Exp</th>
                          <th/><th/><th/>
                        </tr></thead>
                        <tbody>
                          {bulkStudentRows.filter(row=>!bulkSearch||row.full_name?.toLowerCase().includes(bulkSearch.toLowerCase())||row.register_number?.toLowerCase().includes(bulkSearch.toLowerCase())||(row.pkg?.course_name||'').toLowerCase().includes(bulkSearch.toLowerCase())).map((row,i)=>{
                            const expTotal=row.nactet+row.ndt+row.exp;
                            const colTotal=Number(row.col_nactet||0)+Number(row.col_ndt||0)+Number(row.col_exp||0);
                            const status=!row.collect?'none':colTotal<=0?'zero':colTotal>=expTotal&&expTotal>0?'full':expTotal===0?'full':'partial';
                            return(
                            <tr key={row.id} style={{borderTop:'1px solid var(--border)',background:i%2?'var(--surface)':''}}>
                              <td style={{padding:'3px 6px',textAlign:'center'}}>
                                <input type='checkbox' checked={row.collect} onChange={e=>setBulkStudentRows(r=>r.map(s=>s.id===row.id?{...s,collect:e.target.checked}:s))}/>
                              </td>
                              <td style={{padding:'3px 8px',fontWeight:500}}>{row.full_name}</td>
                              <td style={{padding:'3px 8px',fontFamily:'monospace',fontSize:10}}>{row.register_number}</td>
                              <td style={{padding:'3px 8px',color:'var(--accent)',fontSize:10}}>{row.pkg?.course_name||'—'}</td>
                              <td style={{padding:'3px 6px',textAlign:'center',color:'var(--text3)'}}>{row.nactet||'—'}</td>
                              <td style={{padding:'3px 6px',textAlign:'center',color:'var(--text3)'}}>{row.ndt||'—'}</td>
                              <td style={{padding:'3px 6px',textAlign:'center',color:'var(--text3)'}}>{row.exp||'—'}</td>
                              <td style={{padding:'2px 4px'}}><input type='number' min='0' disabled={!row.collect} value={row.col_nactet} onChange={e=>setBulkStudentRows(r=>r.map(s=>s.id===row.id?{...s,col_nactet:Number(e.target.value)}:s))} style={{width:40,fontSize:11,padding:'1px 4px',textAlign:'center'}}/></td>
                              <td style={{padding:'2px 4px'}}><input type='number' min='0' disabled={!row.collect} value={row.col_ndt} onChange={e=>setBulkStudentRows(r=>r.map(s=>s.id===row.id?{...s,col_ndt:Number(e.target.value)}:s))} style={{width:40,fontSize:11,padding:'1px 4px',textAlign:'center'}}/></td>
                              <td style={{padding:'2px 4px'}}><input type='number' min='0' disabled={!row.collect} value={row.col_exp} onChange={e=>setBulkStudentRows(r=>r.map(s=>s.id===row.id?{...s,col_exp:Number(e.target.value)}:s))} style={{width:40,fontSize:11,padding:'1px 4px',textAlign:'center'}}/></td>
                              <td style={{padding:'2px 4px'}}><DateInput disabled={!row.collect} value={row.date} onChange={e=>setBulkStudentRows(r=>r.map(s=>s.id===row.id?{...s,date:e.target.value}:s))} style={{fontSize:10,padding:'1px 4px'}}/></td>
                              <td style={{padding:'3px 6px',textAlign:'center'}}>
                                {!row.collect?<span style={{fontSize:10,color:'var(--text3)'}}>—</span>
                                :status==='zero'?<span style={{fontSize:10,color:'var(--text3)'}}>0 — skipped</span>
                                :status==='full'?<span style={{fontSize:10,background:'var(--green-light)',color:'var(--green)',padding:'1px 6px',borderRadius:4,fontWeight:600}}>✅ Full</span>
                                :<span style={{fontSize:10,background:'rgba(245,158,11,0.1)',color:'var(--amber)',padding:'1px 6px',borderRadius:4,fontWeight:600}}>⚡ Partial</span>}
                              </td>
                              <td style={{padding:'2px 4px'}}>
                                <input type='text' disabled={!row.collect} value={row.remarks||''} onChange={e=>setBulkStudentRows(r=>r.map(s=>s.id===row.id?{...s,remarks:e.target.value}:s))} placeholder='Optional...' style={{fontSize:10,padding:'1px 6px',width:120}}/>
                              </td>
                            </tr>);
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {bulkStudentRows.length===0&&bulkCollectedBatch&&<div style={{textAlign:'center',padding:24,color:'var(--text3)'}}>Loading students...</div>}
                  {!bulkCollectedBatch&&<div style={{textAlign:'center',padding:24,color:'var(--text3)'}}>Select a batch to start</div>}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={()=>{setShowBulkCollected(false);setBulkBatchStudents([]);setBulkStudentRows([])}}>Cancel</button>
                  <button className="btn btn-primary" onClick={bulkMarkBatchCollected} disabled={bulkCollectedSaving||!bulkCollectedBatch||bulkStudentRows.filter(r=>r.collect).length===0}>
                    {bulkCollectedSaving?'Saving...':`💾 Save ${bulkStudentRows.filter(r=>r.collect).length} Records`}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Batch Summary */}
          {distributions.length>0&&(
            <div style={{marginBottom:16,background:'var(--surface)',border:'1px solid var(--border)',borderRadius:10,padding:16}}>
              <div style={{fontWeight:700,fontSize:13,marginBottom:12,color:'var(--accent)'}}>📊 Batch-wise Certificate Summary</div>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead><tr style={{background:'var(--surface2)'}}>
                  <th style={{padding:'6px 10px',textAlign:'left'}}>Batch</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>Total</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>NACTET</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>NDT</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>Collected ✅</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>Not Collected ❌</th>
                  <th style={{padding:'6px 10px',textAlign:'center'}}>Collection %</th>
                </tr></thead>
                <tbody>
                  {(()=>{
                    const byBatch={};
                    distributions.forEach(d=>{
                      const b=d.students?.batches?.batch_name||d.students?.batch_name||'Unknown';
                      if(!byBatch[b])byBatch[b]={total:0,nactet:0,ndt:0,collected:0};
                      byBatch[b].total++;
                      if((d.cert_type||'').toLowerCase().includes('nactet'))byBatch[b].nactet++;
                      if((d.cert_type||'').toLowerCase().includes('ndt'))byBatch[b].ndt++;
                      if(d.collected_by_student)byBatch[b].collected++;
                    });
                    return Object.entries(byBatch).sort((a,b)=>b[0].localeCompare(a[0])).map(([batch,s])=>(
                      <tr key={batch} style={{borderTop:'1px solid var(--border)',cursor:'pointer'}}
                        onClick={()=>setDistSearch(batch)}>
                        <td style={{padding:'6px 10px',fontWeight:600}}>{batch}</td>
                        <td style={{padding:'6px 10px',textAlign:'center'}}>{s.total}</td>
                        <td style={{padding:'6px 10px',textAlign:'center'}}>{s.nactet||'—'}</td>
                        <td style={{padding:'6px 10px',textAlign:'center'}}>{s.ndt||'—'}</td>
                        <td style={{padding:'6px 10px',textAlign:'center',color:'var(--green)',fontWeight:600}}>{s.collected}</td>
                        <td style={{padding:'6px 10px',textAlign:'center',color:s.total-s.collected>0?'var(--red)':'var(--green)',fontWeight:600}}>{s.total-s.collected}</td>
                        <td style={{padding:'6px 10px',textAlign:'center'}}>
                          <div style={{background:'var(--surface2)',borderRadius:4,overflow:'hidden',height:16,width:80,display:'inline-block'}}>
                            <div style={{background:s.collected/s.total>0.8?'var(--green)':'var(--amber)',width:(s.collected/s.total*100)+'%',height:'100%'}}/>
                          </div>
                          <span style={{marginLeft:6}}>{Math.round(s.collected/s.total*100)}%</span>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:8}}>💡 Click a row to filter by batch</div>
            </div>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th><input type='checkbox' checked={filteredDist.length>0&&filteredDist.every(d=>selectedDistIds.includes(d.id))} onChange={e=>{
                    if(e.target.checked) setSelectedDistIds(filteredDist.map(d=>d.id));
                    else setSelectedDistIds([]);
                  }} title='Select All'/></th>
                  <th>#</th><th>Student</th><th>Certificate Type</th><th>Request No.</th><th>Request Date</th><th>Issue Date</th><th>Issuer</th><th>Collected?</th><th>Collection Date</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDist.length === 0 ? (
                  <tr><td colSpan={11}><div className="empty-state"><Award /><h3>No distribution records</h3><p>Click "Add Distribution" to record a handover</p></div></td></tr>
                ) : filteredDist.map((d, i) => (
                  <tr key={d.id} style={{background:!d.collected_by_student&&d.issue_date&&Math.floor((Date.now()-new Date(d.issue_date).getTime())/86400000)>7?'rgba(239,68,68,0.04)':undefined}}>
                    <td style={{padding:'6px 8px'}}>
                      <input type='checkbox' checked={selectedDistIds.includes(d.id)} onChange={e=>{
                        const next = selectedDistIds.filter(id=>id!==d.id);
                        if(e.target.checked) next.push(d.id);
                        setSelectedDistIds(next);
                      }}/>
                    </td>
                    <td style={{ color: 'var(--text3)', fontSize: 12 }}>{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{d.students?.full_name}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{d.students?.register_number}</div>
                    </td>
                    <td><span className={`badge ${certBadge(d.cert_type)}`} style={{ fontSize: 11 }}>{certLabel(d.cert_type)}</span></td>
                    <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{requestNoFor(d)}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(d.request_date)}</td>
                    <td style={{ fontSize: 12 }}>{fmtDate(d.issue_date)}</td>
                    <td style={{ fontSize: 12 }}>{d.issuer_name || '—'}</td>
                    <td>
                      <span className={`badge ${d.collected_by_student ? 'badge-green' : 'badge-amber'}`}>{d.collected_by_student ? '✓ Yes' : (d.quantity && Number(d.quantity_collected) > 0 ? 'Partial' : 'Pending')}</span>
                      {d.quantity ? <div style={{fontSize:10,color:'var(--text3)'}}>{d.quantity_collected || 0} / {d.quantity}</div> : null}
                      {!d.collected_by_student && d.issue_date && (() => {
                        const days = Math.floor((Date.now()-new Date(d.issue_date).getTime())/86400000);
                        return days>7 ? <div style={{fontSize:9,color:'var(--red)',fontWeight:700}}>🚨 {days}d pending</div> : null;
                      })()}
                    </td>
                    <td style={{ fontSize: 12 }}>{fmtDMY(d.student_collection_date) || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-sm" title="View" onClick={() => setViewDist(d)}><Eye size={13} /></button>
                        {canEdit('certificates') && <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEditDist(d)}><Edit2 size={13} /></button>}
                        {canEdit('certificates') && <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} title="Delete" onClick={() => deleteDist(d.id)}><Trash2 size={13} /></button>}
                      {!d.collected_by_student && d.students?.whatsapp_number && (
                        <button className="btn btn-ghost btn-sm" style={{color:'#25d366',fontSize:11}} title="WhatsApp reminder"
                          onClick={()=>{
                            const msg = `*Certificate Collection Reminder — Armstech Engineers*\n`+
                              `Dear ${d.students?.full_name||'Student'},\n\n`+
                              `Your certificate (${d.cert_type||''}) is ready for collection.\n`+
                              `Please collect it from the Armstech office at your earliest convenience.\n\n`+
                              `_Armstech Engineers Pvt Ltd_`;
                            navigator.clipboard.writeText(msg).then(()=>toast.success('Reminder copied!'));
                          }}>💬</button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── VIEW REQUEST MODAL ── */}
      {viewItem && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewItem(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{viewItem.request_number}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewItem(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {/* Group info */}
              {(() => {
                const groupKey = viewItem.cert_group_number || viewItem.request_number;
                const groupItems = certs.filter(c => (c.cert_group_number||c.request_number) === groupKey);
                const fields = [
                  ['Request No.', groupKey],
                  ['Certificate Type', certLabel(viewItem.cert_type)],
                  ['Request Date', viewItem.request_date],
                  ['Issue Date', viewItem.issue_date],
                  ['Course Complete Date', viewItem.course_complete_date],
                  ['Exam End Date', viewItem.exam_end_date],
                  ['Status', statusLabel(viewItem.processing_status)],
                  ['Received Date', viewItem.certificate_received_date],
                  ['Dispatch Mode', viewItem.dispatch_mode],
                  ['Courier/Tracking', viewItem.courier_name ? `${viewItem.courier_name} — ${viewItem.tracking_number||''}` : null],
                  ['Cert Status', viewItem.cert_status !== 'active' ? viewItem.cert_status : null],
                  ['Purpose', viewItem.purpose],
                  ['Remarks', viewItem.remarks],
                ];
                return (
                  <>
                    {fields.map(([label, val]) => val ? (
                      <div key={label} className="detail-row">
                        <div className="detail-label">{label}</div>
                        <div className="detail-value">{val}</div>
                      </div>
                    ) : null)}
                    {/* Students in group */}
                    <div className="detail-row" style={{alignItems:'flex-start'}}>
                      <div className="detail-label">Students ({groupItems.length})</div>
                      <div className="detail-value">
                        {groupItems.map((c,i) => (
                          <div key={c.id} style={{padding:'4px 0',borderBottom:'1px solid var(--border)',fontSize:13}}>
                            <span style={{fontWeight:500}}>{c.students?.full_name}</span>
                            <span style={{marginLeft:8,fontFamily:'monospace',fontSize:11,color:'var(--text3)'}}>{c.students?.register_number}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                );
              })()}
              {renderHistory(history)}
            </div>
            <div className="modal-footer">
              {canEdit('certificates') && isCertAdmin && <button className="btn btn-secondary" onClick={() => {
                const gk = viewItem.cert_group_number || viewItem.request_number;
                const items = certs.filter(c => (c.cert_group_number || c.request_number) === gk);
                setViewItem(null);
                openEditReq({ items: items.length ? items : [viewItem] });
              }}><Edit2 size={13} /> Edit</button>}
              <button className="btn btn-primary" onClick={() => setViewItem(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW DISTRIBUTION MODAL ── */}
      {viewDist && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setViewDist(null)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">Distribution Record</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setViewDist(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              {[
                ['Student', (viewDist.students?.full_name || '') + ' (' + (viewDist.students?.register_number || '') + ')'],
                ['Certificate Type', certLabel(viewDist.cert_type)],
                ['Request Date', viewDist.request_date],
                ['Issue Date', viewDist.issue_date],
                ['Issuer', viewDist.issuer_name],
                ['Collecting Person', viewDist.collector_name],
                ['Collector Contact', viewDist.collector_contact],
                ['Collected by Student', viewDist.collected_by_student ? 'Yes' : 'No'],
                ['Collection Date', viewDist.student_collection_date],
                ['Quantity (issued / collected)', viewDist.quantity ? `${viewDist.quantity_collected || 0} / ${viewDist.quantity}` : null],
                ['ID Proof', viewDist.id_proof_type ? `${viewDist.id_proof_type}${viewDist.id_proof_number ? ' — ' + viewDist.id_proof_number : ''}` : null],
                ['Remarks', viewDist.remarks],
              ].map(([label, val]) => val ? (
                <div key={label} className="detail-row">
                  <div className="detail-label">{label}</div>
                  <div className="detail-value">{val}</div>
                </div>
              ) : null)}
              {renderHistory(distHistory)}
            </div>
            <div className="modal-footer">
              {canEdit('certificates') && <button className="btn btn-secondary" onClick={() => { setViewDist(null); openEditDist(viewDist); }}><Edit2 size={13} /> Edit</button>}
              <button className="btn btn-primary" onClick={() => setViewDist(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD/EDIT REQUEST MODAL ── */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Edit Certificate Request' : 'New Certificate Request'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">

              {/* Step 1 & 2 in a grid */}
              <div className="form-grid" style={{ marginBottom: 16 }}>
                <div className="form-group">
                  <label>Certificate Type</label>
                  <select value={form.cert_type || ''} onChange={e => setForm(f => ({ ...f, cert_type: e.target.value }))}>
                    <option value="">Exam certificate (NACTET / NDT)</option>
                    {CERT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>

                {/* Step 1: Batch */}
                <div className="form-group">
                  <label>Step 1 — Select Batch *</label>
                  <SearchableSelect
                    options={batches.filter(b => !allowedBatchNames || allowedBatchNames.includes(b.batch_name)).map(b=>({value:b.batch_name,label:b.batch_name}))}
                    value={modalBatch}
                    onChange={v=>handleModalBatchChange(v)}
                    placeholder="— Search & Select Batch —"
                    searchPlaceholder="Type batch name..."
                  />
                </div>

                {/* Step 2: Linked Exam Request */}
                <div className="form-group">
                  <label>Linked Exam Request</label>
                  <select value={form.exam_request_id || ''} onChange={e => handleExamReqChange(e.target.value)}>
                    <option value="">— Select Exam Request —</option>
                    {(editId ? examReqs : filteredExamReqs.length > 0 ? filteredExamReqs : []).map(r => (
                      <option key={r.id} value={r.id}>
                        {r.request_number}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Certificate Request Number — shown as separate field */}
                {form.exam_request_id && (
                  <div className="form-group">
                    <label>Certificate Request Number</label>
                    <input
                      value={editId ? (editGroupItems[0]?.cert_group_number || editGroupItems[0]?.request_number || '') : getCertReqNumber(form.exam_request_id, selectedStudentIds.length || (form.student_id ? 1 : 0))}
                      readOnly
                      style={{ fontFamily: 'monospace', fontWeight: 700, color: 'var(--accent)', background: 'var(--accent-light)', borderColor: 'var(--accent)', fontSize: 15 }}
                    />
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>
                      Auto-generated based on this exam request's batch numbering ({selectedStudentIds.length || (form.student_id ? 1 : 0)} of {(examReqs.find(r=>r.id===form.exam_request_id)?.exam_request_students||[]).length} students selected)
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Request Date</label>
                  <DateInput value={form.request_date} onChange={e => setForm(f => ({ ...f, request_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Course Completion Date {form.exam_request_id && <span style={{ fontSize: 10, color: 'var(--accent)' }}>● Auto-filled</span>}</label>
                  <DateInput value={form.course_complete_date} onChange={e => setForm(f => ({ ...f, course_complete_date: e.target.value }))}
                    style={form.course_complete_date && form.exam_request_id ? { background: 'var(--accent-light)', borderColor: 'var(--accent)' } : {}}
                  />
                </div>

                <div className="form-group">
                  <label>Exam End Date {form.exam_request_id && <span style={{ fontSize: 10, color: 'var(--accent)' }}>● Auto-filled</span>}</label>
                  <DateInput value={form.exam_end_date} onChange={e => setForm(f => ({ ...f, exam_end_date: e.target.value }))}
                    style={form.exam_end_date && form.exam_request_id ? { background: 'var(--accent-light)', borderColor: 'var(--accent)' } : {}}
                  />
                </div>

                <div className="form-group">
                  <label>Certificate Issue Date</label>
                  <DateInput value={form.cert_issue_date} onChange={e => setForm(f => ({ ...f, cert_issue_date: e.target.value }))} />
                </div>

                <div className="form-group">
                  <label>Total No. of NACTET Certificates</label>
                  <input type="number" min="0" value={form.total_nactet_certs || ''} onChange={e => setForm(f => ({ ...f, total_nactet_certs: e.target.value }))} placeholder="0" />
                </div>

                <div className="form-group">
                  <label>Total No. of NDT Certificates</label>
                  <input type="number" min="0" value={form.total_ndt_certs || ''} onChange={e => setForm(f => ({ ...f, total_ndt_certs: e.target.value }))} placeholder="0" />
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select value={form.processing_status} onChange={e => setForm(f => ({ ...f, processing_status: e.target.value }))}>
                    <option value="submitted">Submitted</option>
                    <option value="PROCESSED">Processed</option>
                    <option value="collected">At Office</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Experience Certificate Created (Y/N)</label>
                  <select value={form.experience_cert_created || 'no'} onChange={e => setForm(f => ({ ...f, experience_cert_created: e.target.value }))}>
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Concerned Person</label>
                  <SearchableSelect
                    options={coordinators.filter(s => ['Academic Counselor','Office Admin'].includes(s.designation)).map(s => ({ value: s.id, label: s.staff_name + ' (' + s.designation + ')' }))}
                    value={form.concerned_person || ''}
                    onChange={v => setForm(f => ({ ...f, concerned_person: v }))}
                    placeholder="Select Staff"
                    searchPlaceholder="Search staff..."
                  />
                </div>

                <div className="form-group full">
                  <label>Purpose / Remarks</label>
                  <textarea value={form.purpose} onChange={e => setForm(f => ({ ...f, purpose: e.target.value }))} rows={2} />
                </div>

                {/* Dispatch Tracking */}
                <div className="form-group">
                  <label>Dispatch Mode</label>
                  <select value={form.dispatch_mode||'collect'} onChange={e=>setForm(f=>({...f,dispatch_mode:e.target.value}))}>
                    <option value="collect">Student Collects</option>
                    <option value="courier">Courier</option>
                    <option value="post">Post</option>
                  </select>
                </div>
                {(form.dispatch_mode==='courier'||form.dispatch_mode==='post')&&<>
                  <div className="form-group">
                    <label>Courier Name</label>
                    <input value={form.courier_name||''} onChange={e=>setForm(f=>({...f,courier_name:e.target.value}))} placeholder="e.g. BlueDart, DTDC"/>
                  </div>
                  <div className="form-group">
                    <label>Tracking Number</label>
                    <input value={form.tracking_number||''} onChange={e=>setForm(f=>({...f,tracking_number:e.target.value}))} placeholder="Tracking ID"/>
                  </div>
                  <div className="form-group">
                    <label>Dispatch Date</label>
                    <DateInput value={form.dispatch_date||''} onChange={e=>setForm(f=>({...f,dispatch_date:e.target.value}))}/>
                  </div>
                </>}

                {/* Returned / Lost */}
                <div className="form-group">
                  <label>Certificate Status</label>
                  <select value={form.cert_status||'active'} onChange={e=>setForm(f=>({...f,cert_status:e.target.value}))}>
                    <option value="active">Active</option>
                    <option value="returned">Returned</option>
                    <option value="lost">Lost / Missing</option>
                  </select>
                </div>
                {(form.cert_status==='returned'||form.cert_status==='lost')&&(
                  <div className="form-group">
                    <label>Status Notes</label>
                    <input value={form.cert_status_notes||''} onChange={e=>setForm(f=>({...f,cert_status_notes:e.target.value}))} placeholder="Reason or details..."/>
                  </div>
                )}
              </div>

              {/* Step 3 — Student Table */}
              <div style={{ borderTop: '2px solid var(--border)', paddingTop: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>
                      Step 3 — Select Students (multiple)
                      {form.student_id && <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--green)', fontWeight: 500 }}>✓ Selected</span>}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                    {form.exam_request_id && evalStudents.length === 0
                        ? '⚠️ No students have marks entered for this exam request'
                        : form.exam_request_id
                          ? `${evalStudents.length} student(s) with marks entered`
                          : 'Select an exam request above to see eligible students'}
                    </div>
                  </div>
                  {form.student_id && (
                    <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }} onClick={() => setForm(f => ({ ...f, student_id: '' }))}>
                      ✕ Clear
                    </button>
                  )}
                </div>

                {form.exam_request_id && evalStudents.length > 0 && (
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: 'var(--accent-light)' }}>
                          <th style={{ width: 40, padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>✓</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>Reg No.</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>Student Name</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>Batch</th>
                          <th style={{ padding: '8px 12px', textAlign: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>Attempt</th>
                          <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase' }}>Cert Request No.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {evalStudents.map(s => {
                          const isSelected = selectedStudentIds.includes(s.id);
                          const previewCount = (selectedStudentIds.includes(s.id) ? selectedStudentIds.length : selectedStudentIds.length + 1) || 1;
                          const certNo = editId
                            ? (editGroupItems[0]?.cert_group_number || editGroupItems[0]?.request_number || getCertReqNumber(form.exam_request_id, previewCount))
                            : getCertReqNumber(form.exam_request_id, previewCount);
                          return (
                            <tr
                              key={s.id}
                              onClick={() => {
                                setSelectedStudentIds(prev =>
                                  prev.includes(s.id) ? prev.filter(id => id !== s.id) : [...prev, s.id]
                                );
                              }}
                              style={{
                                cursor: 'pointer',
                                background: isSelected ? 'var(--accent-light)' : 'transparent',
                                borderTop: '1px solid var(--border)',
                                transition: 'background 0.1s',
                              }}
                              onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = 'var(--bg3)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = isSelected ? 'var(--accent-light)' : 'transparent'; }}
                            >
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <div style={{ width: 20, height: 20, borderRadius: 4, border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`, background: isSelected ? 'var(--accent)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto' }}>
                                  {isSelected && <span style={{ color: '#fff', fontSize: 12, fontWeight: 700 }}>✓</span>}
                                </div>
                              </td>
                              <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 13, color: 'var(--accent)', fontWeight: 600 }}>{s.register_number}</td>
                              <td style={{ padding: '10px 12px', fontSize: 13, fontWeight: isSelected ? 600 : 400 }}>{s.full_name}</td>
                              <td style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>{s.batch_name || '—'}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span className={`badge ${s._attemptNumber > 1 ? 'badge-amber' : 'badge-green'}`}>
                                  {s._attemptNumber === 1 ? '1st' : s._attemptNumber === 2 ? '2nd' : s._attemptNumber === 3 ? '3rd' : `${s._attemptNumber}th`}
                                </span>
                              </td>
                              <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>{certNo}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* If no exam request selected, show searchable student dropdown */}
                {!form.exam_request_id && (
                  <SearchableSelect
                    options={students.filter(s => (!modalBatch || s.batch_name === modalBatch) && s.student_status !== 'Discontinued').sort(byRegNo).map(s => ({ value: s.id, label: `${s.register_number} — ${s.full_name}` }))}
                    value={form.student_id}
                    onChange={v => setForm(f => ({ ...f, student_id: v }))}
                    placeholder="Select Student"
                    searchPlaceholder="Search student..."
                  />
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveRequest} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Create Request'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD/EDIT DISTRIBUTION MODAL ── */}
      {showDistModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowDistModal(false)}>
          <div className="modal modal-lg">
            <div className="modal-header">
              <span className="modal-title">{editDistId ? 'Edit Distribution' : 'Add Certificate Distribution'}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowDistModal(false)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <div className="form-section-heading">Certificate Details</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Filter by Batch</label>
                  <SearchableSelect
                    options={batches.map(b => ({ value: b.batch_name, label: b.batch_name }))}
                    value={distForm.batch_id || ''}
                    onChange={v => setDistForm(f => ({ ...f, batch_id: v, student_id: '' }))}
                    placeholder="All Batches"
                    searchPlaceholder="Search batch..."
                  />
                </div>
                <div className="form-group">
                  <label>Student *</label>
                  <SearchableSelect
                    options={(distForm.batch_id ? students.filter(s => s.batch_name === distForm.batch_id && s.student_status !== 'Discontinued') : students.filter(s => s.student_status !== 'Discontinued')).sort(byRegNo).map(s => ({ value: s.id, label: `${s.register_number} — ${s.full_name}` }))}
                    value={distForm.student_id}
                    onChange={v => setDistForm(f => ({ ...f, student_id: v }))}
                    placeholder="Select Student"
                    searchPlaceholder="Search student..."
                  />
                </div>
                <div className="form-group">
                  <label>Certificate Type *</label>
                  <SearchableSelect
                    options={CERT_DIST_TYPES.map(t => ({ value: t, label: t }))}
                    value={distForm.cert_type || ''}
                    onChange={v => setDistForm(f => ({ ...f, cert_type: v }))}
                    placeholder="Select Certificate Type"
                    searchPlaceholder="Search type..."
                  />
                </div>
                <div className="form-group">
                  <label>Request Date</label>
                  <DateInput value={distForm.request_date} onChange={e => setDistForm(f => ({ ...f, request_date: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Issue Date</label>
                  <DateInput value={distForm.issue_date} onChange={e => setDistForm(f => ({ ...f, issue_date: e.target.value }))} />
                </div>
                <div className="form-group full">
                  <label>Issuer Name</label>
                  <select value={distForm.issuer_name} onChange={e => setDistForm(f => ({ ...f, issuer_name: e.target.value }))}>
                    <option value="">Select Issuer</option>
                    {ISSUER_NAMES.map(name => <option key={name} value={name}>{name}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-section-heading" style={{ marginTop: 20 }}>Collection Details</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Collecting Person Name</label>
                  <input value={distForm.collector_name} onChange={e => setDistForm(f => ({ ...f, collector_name: e.target.value }))} placeholder="Who will collect the certificate" />
                </div>
                <div className="form-group">
                  <label>Collector Contact</label>
                  <input value={distForm.collector_contact} onChange={e => setDistForm(f => ({ ...f, collector_contact: e.target.value }))} placeholder="Phone number" />
                </div>
                <div className="form-group">
                  <label>Collected by Student?</label>
                  <select value={distForm.collected_by_student ? 'yes' : 'no'} onChange={e => setDistForm(f => ({ ...f, collected_by_student: e.target.value === 'yes' }))}>
                    <option value="no">No — pending collection</option>
                    <option value="yes">Yes — collected</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Collected Date</label>
                  <DateInput value={distForm.student_collection_date} onChange={e => setDistForm(f => ({ ...f, student_collection_date: e.target.value, collected_by_student: e.target.value ? true : f.collected_by_student }))} />
                </div>
                {distExtra && <>
                  <div className="form-group">
                    <label>Quantity Issued</label>
                    <input type="number" min="0" value={distForm.quantity} onChange={e => setDistForm(f => ({ ...f, quantity: e.target.value }))} placeholder="No. of certificates" />
                  </div>
                  <div className="form-group">
                    <label>Quantity Collected</label>
                    <input type="number" min="0" value={distForm.quantity_collected} onChange={e => setDistForm(f => ({ ...f, quantity_collected: e.target.value, collected_by_student: (f.quantity !== '' && e.target.value !== '' && Number(e.target.value) >= Number(f.quantity)) ? true : f.collected_by_student }))} placeholder="Handed over" />
                  </div>
                  <div className="form-group">
                    <label>ID Proof Shown</label>
                    <select value={distForm.id_proof_type} onChange={e => setDistForm(f => ({ ...f, id_proof_type: e.target.value }))}>
                      <option value="">— None —</option>
                      {ID_PROOF_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>ID Proof Number (last digits)</label>
                    <input value={distForm.id_proof_number} onChange={e => setDistForm(f => ({ ...f, id_proof_number: e.target.value }))} placeholder="e.g. last 4 digits" />
                  </div>
                </>}
                <div className="form-group full">
                  <label>Remarks</label>
                  <textarea value={distForm.remarks} onChange={e => setDistForm(f => ({ ...f, remarks: e.target.value }))} placeholder="Any additional notes..." rows={2} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDistModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveDist} disabled={distSaving}>{distSaving ? 'Saving...' : editDistId ? 'Update' : 'Save Distribution'}</button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRM ACTION POPUP */}
      {confirmAction && (
        <div className="modal-overlay" onClick={() => setConfirmAction(null)}>
          <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Confirm {confirmAction.label}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setConfirmAction(null)}><X size={16} /></button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '28px 24px' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>
                {confirmAction.status === 'PROCESSED' ? '📋' : '✅'}
              </div>
              <div style={{ fontSize: 15, color: 'var(--text2)', padding: '12px 16px', background: 'var(--bg3)', borderRadius: 8 }}>
                Are you confirm to mark this as <strong>{confirmAction.label}</strong>?
              </div>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'center', gap: 12 }}>
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>Cancel</button>
              <button
                className={`btn ${confirmAction.status === 'PROCESSED' ? 'btn-success' : 'btn-primary'}`}
                onClick={async () => {
                  const ids = confirmAction.ids || [confirmAction.id];
                  await applyStatusChange(ids, confirmAction.status);
                  setFilterTab(confirmAction.tab);
                  setSelectedIds([]);
                  setConfirmAction(null);
                }}
              >
                ✓ Yes, Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* WhatsApp Notification Modal */}
      {showWAModal && (() => {
        const processedDists = waDists;
        return (
          <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowWAModal(false)}>
            <div className="modal modal-lg">
              <div className="modal-header">
                <span className="modal-title">📱 Certificate Ready — WhatsApp Notification</span>
                <button className="btn btn-ghost btn-sm" onClick={()=>setShowWAModal(false)}><X size={16}/></button>
              </div>
              <div className="modal-body">
                <div className="form-group" style={{marginBottom:12}}>
                  <label>Message Template (customize before sending)</label>
                  <textarea value={waMessage} onChange={e=>setWAMessage(e.target.value)} rows={4} style={{fontSize:13}}/>
                  <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>Tip: [Name] will be replaced with student name when sending</div>
                </div>
                <div style={{fontSize:13,fontWeight:600,marginBottom:8,color:'var(--text2)'}}>
                  {processedDists.length} students pending collection:
                </div>
                {processedDists.length === 0 ? (
                  <div className="empty-state"><p>No students pending collection</p></div>
                ) : (
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                    <thead><tr style={{background:'var(--accent-light)'}}>
                      <th style={{padding:'8px 12px',textAlign:'left'}}>#</th>
                      <th style={{padding:'8px 12px',textAlign:'left'}}>Student</th>
                      <th style={{padding:'8px 12px',textAlign:'left'}}>Contact</th>
                      <th style={{padding:'8px 12px',textAlign:'center'}}>Send</th>
                    </tr></thead>
                    <tbody>{processedDists.map((d,i)=>{
                      const stu = students.find(s=>s.id===d.student_id) || d.students || distributions.find(dist=>dist.student_id===d.student_id)?.students;
                      const phone = stu?.whatsapp_number || stu?.contact_number || d.students?.contact_number || '';
                      const name = stu?.full_name || 'Student';
                      const msg = waMessage.replace(/\[Name\]/g, name);
                      const clean = waPhone(phone);
                      const waUrl = clean ? 'https://wa.me/'+clean+'?text='+encodeURIComponent(msg) : null;
                      return (
                        <tr key={d.id} style={{borderTop:'1px solid var(--border)',background:i%2?'var(--bg3)':'transparent'}}>
                          <td style={{padding:'8px 12px',color:'var(--text3)'}}>{i+1}</td>
                          <td style={{padding:'8px 12px'}}>
                            <div style={{fontWeight:600}}>{name}</div>
                            <div style={{fontSize:11,color:'var(--text3)',fontFamily:'monospace'}}>{stu?.register_number||'—'}</div>
                          </td>
                          <td style={{padding:'8px 12px',fontFamily:'monospace',fontSize:12}}>{phone||<span style={{color:'var(--red)'}}>No number</span>}</td>
                          <td style={{padding:'8px 12px',textAlign:'center'}}>
                            {waUrl
                              ? <a href={waUrl} target="_blank" rel="noopener noreferrer" style={{background:'#25D366',color:'#fff',textDecoration:'none',padding:'5px 14px',borderRadius:6,fontSize:12,display:'inline-block'}}>📱 Send</a>
                              : <span style={{color:'var(--text3)',fontSize:11}}>No number</span>
                            }
                          </td>
                        </tr>
                      );
                    })}</tbody>
                  </table>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={()=>setShowWAModal(false)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Bulk Collect Modal */}
      {showDistBulkModal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setShowDistBulkModal(false)}>
          <div className="modal" style={{maxWidth:380}}>
            <div className="modal-header">
              <span className="modal-title">✅ Mark {selectedDistIds.length} Certificates as Collected</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowDistBulkModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Collection Date</label>
                <DateInput value={bulkCollectDate} onChange={e=>setBulkCollectDate(e.target.value)}/>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={()=>setShowDistBulkModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={bulkMarkCollected}>Mark as Collected</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
